/**
 * 全局音频管理器
 * 负责管理全局音频资源和设备切换
 * 
 * 设计特点：
 * - 单例模式，全局唯一实例
 * - 使用用户中心化的WebSocket架构
 * - 负责设备间的智能切换
 * - 维护全局音频上下文
 */

import { MicrophoneModule } from './MicrophoneModule';
import { settingsService } from '../settingsService';
import { authService } from '../authService';
import { mqttService } from '../mqttService';
import { logger } from '../../utils/logger';
import { publicAssetUrl } from '../../utils/publicAssetUrl';
import {
  isVoiceDownstreamCodec2,
  isVoiceDownstreamOpus,
  isVoiceUplinkCodec2,
  type MqttAudioDownstreamKind,
  getCodec2DownstreamFrameBytes,
  getCodec2DownstreamRingInterleavedFloats,
  getCodec2DownstreamMinInterleavedSamples8k,
  getCodec2PlaybackMaxPcmQueueChunks,
  getAudioPlaybackRingBufferFloats,
  getAudioPlaybackMinInterleavedSamples,
  isAudioPlaybackAggressiveRingStart,
  isCodec2DownstreamForceResampleTo48k,
  getCodec2PlaybackPreemphasisCoefficient,
  getCodec2Mode,
  getCodec2UplinkMonoPcmBytesPerFrame,
  getCodec2UplinkMqttBatchFrameCount,
  padUplinkPcmToCodec2FrameMultiple,
} from '../../constants/Codec2Constants';
import type { MqttVoiceUplinkRoute } from './MqttAudioManager';
import { getCodec2DecodeClient, disposeCodec2DecodeClient } from './Codec2DecodeClient';
import { getCodec2EncodeClient, disposeCodec2EncodeClient } from './Codec2EncodeClient';
import { pcmStereo48kToMono8kS16le } from './pcmStereo48kToMono8kS16le';
import { pcmMono8kToStereo48kS16le } from './pcmMono8kToStereo48kS16le';
import { applyCodec2Mono8kPreemphasisS16le } from './pcmMono8kPreemphasisS16le';
import { OpusWebCodecsDownstreamSession, splitOpusMqttPayload } from './opusWebCodecsDownstreamDecoder';

export interface AudioSystemState {
  isInitialized: boolean;
  microphonePermission: 'granted' | 'denied' | 'prompt';
  activeDeviceId: string | null;
  isConnected: boolean;
  isRecording: boolean;
  isListening: boolean;

  devices: {
    [deviceId: string]: {
      isActive: boolean;
      isPlaying: boolean;
      isRecording: boolean;
      connectionStatus: 'connected' | 'disconnected' | 'connecting';
      audioLevel?: number;
    }
  };
}

export interface AudioManagerEvents {
  'initialized': () => void;
  'connected': () => void;
  'disconnected': () => void;
  'activeDeviceChanged': (data: { deviceId: string; previousDeviceId?: string }) => void;
  'deviceStatusChanged': (data: { deviceId: string; status: any }) => void;
  'deviceFrequencyStatusUpdate': (parsedStatus: any) => void;
  'activeDeviceFrequencyStatusUpdate': (parsedStatus: any) => void;
  'microphonePermissionChanged': (permission: 'granted' | 'denied' | 'prompt') => void;
  'connectionChange': (connected: boolean) => void;
  'audioData': (audioData: ArrayBuffer) => void;
  'audioLevel': (level: number) => void;
  'bufferStatus': (status: any) => void;
  'recordingStarted': () => void;
  'recordingStopped': () => void;
  'listeningStarted': (deviceId: string) => void;
  'listeningStopped': () => void;
  'error': (error: Error) => void;
}

export class AudioManager {
  private static instance: AudioManager;
  private audioContext: AudioContext | null = null;
  private microphoneModule: MicrophoneModule | null = null;
  
  // AudioWorklet相关
  private audioWorkletNode: AudioWorkletNode | null = null;
  private isWorkletSupported = false;
  private workletInitialized = false;
  
  // 音频缓冲队列
  private audioBufferQueue: Map<string, ArrayBuffer[]> = new Map();
  // 自适应缓冲策略
  private readonly MIN_AUDIO_DURATION_MS = 80; // 最小音频时长80ms，减少延迟
  private readonly MAX_BUFFER_DURATION_MS = 150; // 最大缓冲时长150ms，平衡延迟和流畅度
  private readonly TARGET_BUFFER_DURATION_MS = 120; // 目标缓冲时长120ms
  // 网络状况监控
  private networkLatency: Map<string, number> = new Map();
  private packetLossRate: Map<string, number> = new Map();
  private lastPacketTime: Map<string, number> = new Map();
  
  // 音频播放状态
  private isPlaying: Map<string, boolean> = new Map();
  private lastPlayTime: Map<string, number> = new Map();
  
  // 用户音频管理
  // TODO: 重构为MQTT架构
  // private userAudioManager: UserAudioManager | null = null;
  private activeDeviceId: string | null = null;
  private userId: string = '1'; // 默认用户ID，将在初始化时更新
  private deviceStatusMqttUnsub: (() => void) | null = null;
  
  // 连续播放相关 - 使用AudioWorklet实现连续播放
  private continuousPlaybackNode: AudioWorkletNode | null = null;
  private isInitializingAudioWorklet = false;
  private audioWorkletInitPromise: Promise<void> | null = null;

  /** Codec2 下行独立 8kHz 上下文（避免 JS 内 8→48 上采样；与录音用主上下文分离） */
  private codec2PlaybackContext: AudioContext | null = null;
  private codec2ContinuousPlaybackNode: AudioWorkletNode | null = null;
  private codec2PlaybackContextUnsupported = false;
  private isInitializingCodec2Playback = false;
  private codec2PlaybackInitPromise: Promise<void> | null = null;

  /** Codec2 下行：MQTT 粘包余量（可变包长：8B、88B 等；不足一帧的尾字节攒到下一包） */
  private codec2DownstreamRemainder = new Map<string, Uint8Array>();

  /**
   * Codec2 下行：每设备解码后 PCM 段 FIFO，串行写入 Worklet 环缓，保证与 MQTT 到达顺序一致。
   * 段数上限由 getCodec2PlaybackMaxPcmQueueChunks() 控制，超出丢最旧。
   */
  private codec2DecodedPcmQueue = new Map<string, ArrayBuffer[]>();
  private codec2DecodedPcmDrainActive = new Map<string, boolean>();

  /** Opus 下行（…/downstream/opus）：每设备一条 WebCodecs 解码时间线 */
  private opusDownstreamSessions = new Map<string, OpusWebCodecsDownstreamSession>();

  /** Codec2 上行独立 8kHz 录音上下文（浏览器原生重采样，音质优于 JS 线性插值） */
  private codec2UplinkRecordingCtx: AudioContext | null = null;
  private codec2UplinkRecordingWorklet: AudioWorkletNode | null = null;
  private codec2UplinkRecordingSource: MediaStreamAudioSourceNode | null = null;
  private codec2UplinkRecordingGain: GainNode | null = null;
  private codec2UplinkRecording8kUnsupported = false;
  /** 标记当前 PTT 录音走的是 8kHz 直采路径（sendAudioToBackend 据此跳过重采样） */
  private uplinkIs8kMono = false;

  /** Codec2 上行：8 kHz mono s16le 粘包余量（须凑满 mode 约定帧长再送 c2enc） */
  private codec2Uplink8kRemainder = new Uint8Array(0);

  /** Codec2 上行：c2enc 输出比特暂存，凑满 N 帧再 MQTT publish（见 getCodec2UplinkMqttBatchFrameCount） */
  private codec2UplinkBitBatch = new Uint8Array(0);

  /** 缓存动态导入的 MqttAudioManager 模块，避免在热路径中反复 await import() */
  private _mqttAudioManagerMod: { getMqttAudioManager: () => import('./MqttAudioManager').MqttAudioManager | null } | null = null;

  /** PTT 期间累积 Codec2 编码比特流，松开后下载为文件 */
  private codec2DumpChunks: Uint8Array[] | null = null;
  /** PTT 期间累积麦克风录音数据（8kHz mono 或 48kHz stereo，取决于采集路径） */
  private rawMicDumpChunks: Uint8Array[] | null = null;

  /**
   * 每设备最近一次下行语音格式（来自 `…/downstream` 与 `…/downstream/c2` 的包），
   * 用于 PTT 上行与下行对齐：收到 c2 主题则该 deviceId 上行走 Codec2，否则 PCM。
   */
  private deviceDownstreamVoiceHint = new Map<string, 'pcm' | 'codec2'>();

  // 状态管理
  private state: AudioSystemState = {
    isInitialized: false,
    microphonePermission: 'prompt',
    activeDeviceId: null,
    isConnected: false,
    isRecording: false,
    isListening: false,

    devices: {}
  };
  
  // 事件管理
  private eventListeners: Map<keyof AudioManagerEvents, Function[]> = new Map();
  
  // 音频数据包计数
  private audioPacketCount = 0;
  
  // 音频数据接收监控
  private audioDataReceivedTimeout: NodeJS.Timeout | null = null;
  private readonly AUDIO_DATA_TIMEOUT_MS = 10000; // 10秒无音频数据则警告
  
  private constructor() {
    // 音频管理器实例创建
  }
  
  /**
   * 获取单例实例
   */
  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }
  
  /**
   * 初始化音频管理器
   */
  async initialize(): Promise<void> {
    try {
      // 简单检查：如果已经初始化且关键组件都存在，则跳过
      if (this.state.isInitialized && this.audioContext && this.microphoneModule) {
        console.log('AudioManager: 音频系统已经完整初始化，跳过重复初始化');
        return;
      }
      
      // 如果状态不一致，重置并重新初始化
      if (this.state.isInitialized) {
        console.log('AudioManager: 检测到状态不一致，重置并重新初始化...');
        await this.dispose();
      }
      
      console.log('AudioManager: 初始化音频系统...');
      
      // 获取当前登录用户ID
      const currentUser = authService.getUser();
      if (currentUser) {
        this.userId = currentUser.id.toString();
        // 使用当前登录用户ID: ${this.userId}
      } else {
        console.warn('AudioManager: 未找到登录用户，使用默认用户ID:', this.userId);
      }
      
      // 修复音频缓冲区大小设置
      settingsService.fixAudioBufferSize();
      
      // 检查浏览器支持
      if (!this.checkBrowserSupport()) {
        throw new Error('浏览器不支持所需的音频API');
      }
      
      // 创建全局音频上下文
      await this.createAudioContext();
      
      // 验证AudioContext是否有效
      if (!this.audioContext) {
        throw new Error('AudioContext创建失败，无法初始化音频系统');
      }
      
      // 创建麦克风模块
      this.microphoneModule = new MicrophoneModule(this.audioContext);
      
      // 设置MicrophoneModule事件监听
      this.setupMicrophoneModuleEvents();
      
      // 初始化MicrophoneModule的AudioWorklet（全局唯一加载 audio-processor.js）
      if (this.isWorkletSupported) {
        await this.microphoneModule.initializeAudioWorklet();
        this.syncWorkletStateFromMicrophoneModule();
      }
      this.logAudioProcessingMode();
      
      // 创建用户音频管理器
      // TODO: 重构为MQTT架构
      // this.userAudioManager = new UserAudioManager(this.userId);
      // this.setupUserAudioManagerEvents();
      
      // 标记为已初始化
      this.state.isInitialized = true;
      
      console.log('AudioManager: 初始化完成');
      this.emit('initialized');
      
    } catch (error) {
      console.error('AudioManager: 音频系统初始化失败:', error);
      this.emit('error', error as Error);
      throw error;
    }
  }
  
  /**
   * 设置MicrophoneModule事件监听
   */
  private setupMicrophoneModuleEvents(): void {
    if (!this.microphoneModule) return;
    
    // console.log('🎤 [测试] 设置MicrophoneModule事件监听器');
    
    // 监听音频数据事件
    this.microphoneModule.on('audioData', (audioData: ArrayBuffer) => {
      // 高频日志关闭：每包音频都会触发
      // console.log('🎤 [测试] AudioManager收到MicrophoneModule的音频数据:', {
      //   audioDataSize: audioData.byteLength,
      //   activeDeviceId: this.state.activeDeviceId,
      //   isRecording: this.state.isRecording
      // });
      
      // 发送音频数据到后端
      if (this.state.activeDeviceId && this.state.isRecording) {
        this.sendAudioToBackend(this.state.activeDeviceId, audioData);
      } else {
        // 高频日志关闭：录音初始化阶段会频繁出现
        // console.log('🎤 [测试] 跳过发送音频数据 - 条件不满足:', {
        //   hasActiveDevice: !!this.state.activeDeviceId,
        //   isRecording: this.state.isRecording
        // });
      }
    });
    
    // 监听音频电平事件
    this.microphoneModule.on('audioLevel', (level: number) => {
      // 更新音频电平
      if (this.state.activeDeviceId) {
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [this.state.activeDeviceId]: {
              ...this.state.devices[this.state.activeDeviceId],
              audioLevel: level
            }
          }
        };
      }
      
      // 触发音频电平事件
      this.emit('audioLevel', level);
    });
    
    // console.log('🎤 [测试] MicrophoneModule事件监听器设置完成');
  }

  /**
   * 设置用户音频管理器事件监听
   */
  private setupUserAudioManagerEvents(): void {
    // TODO: 重构为MQTT架构
    // setupUserAudioManagerEvents - 已禁用，等待MQTT重构
    /*
    if (!this.userAudioManager) return;
    
    this.userAudioManager.on('connected', () => {
      this.state.isConnected = true;
      this.emit('connected');
      this.emit('connectionChange', true);
    });
    
    this.userAudioManager.on('error', (error: Error) => {
      this.emit('error', error);
    });
    
    this.userAudioManager.on('audioData', (audioData: ArrayBuffer) => {
      this.audioPacketCount++;
      this.emit('audioData', audioData);
    });
    
    this.userAudioManager.on('recordingStarted', () => {
      this.state.isRecording = true;
      this.emit('recordingStarted');
    });
    
    this.userAudioManager.on('recordingStopped', () => {
      this.state.isRecording = false;
      this.emit('recordingStopped');
    });
    
    this.userAudioManager.on('listeningStarted', (deviceId: string) => {
      this.state.isListening = true;
      this.activeDeviceId = deviceId;
      this.state.activeDeviceId = deviceId;
      this.emit('listeningStarted', deviceId);
      this.emit('activeDeviceChanged', { deviceId });
    });
    
    this.userAudioManager.on('listeningStopped', () => {
      this.state.isListening = false;
      this.activeDeviceId = null;
      this.state.activeDeviceId = null;
      this.emit('listeningStopped');
    });
    
    this.userAudioManager.on('deviceStatusChange', (message: any) => {
      this.emit('deviceStatusChanged', { deviceId: message.deviceId, status: message });
    });
    
    // 监听设备频率状态更新事件
    this.userAudioManager.on('deviceFrequencyStatusUpdate', (parsedStatus: any) => {
      this.handleDeviceFrequencyStatusUpdate(parsedStatus);
    });
    
    this.userAudioManager.on('reconnectFailed', () => {
      this.state.isConnected = false;
      this.emit('disconnected');
      this.emit('connectionChange', false);
    });
    */
  }
  
  /**
   * 检查浏览器支持
   */
  private checkBrowserSupport(): boolean {
    const hasAudioContext = !!(window.AudioContext || (window as any).webkitAudioContext);
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    const hasWebSocket = !!window.WebSocket;
    
    // 检查AudioWorklet支持
    try {
      this.isWorkletSupported = !!(window.AudioContext && 'audioWorklet' in window.AudioContext.prototype);
    } catch (error) {
      console.warn('AudioManager: AudioWorklet支持检查失败:', error);
      this.isWorkletSupported = false;
    }
    
    console.log('浏览器支持检查:', {
      AudioContext: hasAudioContext,
      getUserMedia: hasGetUserMedia,
      WebSocket: hasWebSocket,
      AudioWorklet: this.isWorkletSupported
    });
    
    return hasAudioContext && hasGetUserMedia && hasWebSocket;
  }
  
  /**
   * 创建全局音频上下文
   */
  private async createAudioContext(): Promise<void> {
    try {
      const audioSettings = settingsService.getAudioSettings();
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: audioSettings.sampleRate
      });
      
      // 处理Chrome的autoplay政策
      if (this.audioContext.state === 'suspended') {
        // 音频上下文被挂起，等待用户交互后恢复
      }
      
      console.log('AudioManager: 音频上下文创建成功 - 状态:', this.audioContext.state);
      
      // audio-processor 仅在 MicrophoneModule.initializeAudioWorklet 中加载一次，避免重复 addModule
      // 导致部分环境下处理器未注册；此处不再单独创建 AudioWorkletNode。
      
      // 显示当前音频处理模式
      this.logAudioProcessingMode();
      
    } catch (error) {
      console.error('AudioManager: 创建音频上下文失败:', error);
      throw error;
    }
  }
  
  /**
   * 尝试创建独立 8kHz AudioContext + Worklet 用于 Codec2 上行录音。
   * 成功返回 true（后续 PTT 走 8kHz 直采）；失败标记不支持并返回 false（回退 48kHz + JS 重采样）。
   */
  private async ensureCodec2UplinkRecordingContext(): Promise<boolean> {
    if (this.codec2UplinkRecording8kUnsupported) return false;
    if (this.codec2UplinkRecordingCtx) return true;

    try {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor({ sampleRate: 8000 });
      if (Math.abs(ctx.sampleRate - 8000) > 60) {
        console.warn('AudioManager: 浏览器不支持 8kHz AudioContext，实际速率', ctx.sampleRate);
        await ctx.close();
        this.codec2UplinkRecording8kUnsupported = true;
        return false;
      }

      await ctx.audioWorklet.addModule(
        (typeof process !== 'undefined' && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : '') + '/audio-processor.js'
      );

      this.codec2UplinkRecordingCtx = ctx;
      console.log('AudioManager: 8kHz Codec2 上行录音上下文已创建');
      return true;
    } catch (e) {
      console.warn('AudioManager: 创建 8kHz 录音上下文失败，回退 48kHz', e);
      this.codec2UplinkRecording8kUnsupported = true;
      return false;
    }
  }

  /** 用 8kHz 上下文开始录音：获取麦克风流 → 连接 Worklet → 监听数据 */
  private async start8kRecording(): Promise<void> {
    const ctx = this.codec2UplinkRecordingCtx;
    if (!ctx) throw new Error('8kHz 上下文不存在');

    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // 获取麦克风流（复用 microphoneModule 的 stream，或重新请求）
    let stream: MediaStream | null = null;
    if (this.microphoneModule) {
      stream = await this.microphoneModule.getMediaStream();
      if (!stream) {
        await this.microphoneModule.requestPermission();
        stream = await this.microphoneModule.getMediaStream();
      }
    }
    if (!stream) {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
    }

    this.codec2UplinkRecordingSource = ctx.createMediaStreamSource(stream);

    this.codec2UplinkRecordingWorklet = new AudioWorkletNode(ctx, 'audio-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      processorOptions: {
        bufferSize: 4096,
        channels: 1,
        sampleRate: 8000,
        uplinkSampleRate: 8000,
        monoDirectMode: true,
        noiseSuppression: true,
        noiseGate: 0.001,
        softClipping: true,
        dithering: true,
      },
    });

    this.codec2UplinkRecordingWorklet.port.onmessage = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type === 'audioData' && data.audioData) {
        const buf: ArrayBuffer = data.audioData instanceof ArrayBuffer
          ? data.audioData
          : data.audioData.buffer.slice(data.audioData.byteOffset, data.audioData.byteOffset + data.audioData.byteLength);
        if (this.state.activeDeviceId && this.state.isRecording) {
          this.sendAudioToBackend(this.state.activeDeviceId, buf);
        }
      }
    };

    // 静音输出（防止 iOS/WebKit 不拉取节点）
    this.codec2UplinkRecordingGain = ctx.createGain();
    this.codec2UplinkRecordingGain.gain.value = 0;
    this.codec2UplinkRecordingWorklet.connect(this.codec2UplinkRecordingGain);
    this.codec2UplinkRecordingGain.connect(ctx.destination);

    this.codec2UplinkRecordingSource.connect(this.codec2UplinkRecordingWorklet);
    this.uplinkIs8kMono = true;
    console.log('AudioManager: 8kHz 单声道录音已启动');
  }

  /** 停止 8kHz 录音（断开节点，保留上下文供下次复用） */
  private stop8kRecording(): void {
    try {
      if (this.codec2UplinkRecordingSource && this.codec2UplinkRecordingWorklet) {
        this.codec2UplinkRecordingSource.disconnect(this.codec2UplinkRecordingWorklet);
      }
    } catch { /* ignore */ }
    this.codec2UplinkRecordingSource = null;
    this.uplinkIs8kMono = false;
  }

  /**
   * 与 MicrophoneModule 共用同一录音 AudioWorklet 节点（避免重复 addModule / 双节点不一致）
   */
  private syncWorkletStateFromMicrophoneModule(): void {
    if (!this.microphoneModule) {
      this.audioWorkletNode = null;
      this.workletInitialized = false;
      return;
    }
    const mic = this.microphoneModule as unknown as {
      workletInitialized: boolean;
      audioWorkletNode: AudioWorkletNode | null;
      isWorkletSupported: boolean;
    };
    if (mic.workletInitialized && mic.audioWorkletNode) {
      this.audioWorkletNode = mic.audioWorkletNode;
      this.workletInitialized = true;
    } else {
      this.audioWorkletNode = null;
      this.workletInitialized = false;
      if (mic.isWorkletSupported === false) {
        this.isWorkletSupported = false;
      }
    }
  }

  /**
   * 在 MicrophoneModule 已就绪时加载录音用 Worklet（重建上下文等场景可复用）
   */
  private async initializeAudioWorklet(): Promise<void> {
    if (!this.audioContext || !this.isWorkletSupported || this.workletInitialized) {
      return;
    }
    if (!this.microphoneModule) {
      return;
    }
    await this.microphoneModule.initializeAudioWorklet();
    this.syncWorkletStateFromMicrophoneModule();
    if (this.workletInitialized) {
      setTimeout(() => {
        this.testAudioWorklet();
      }, 1000);
    }
  }
  
  /**
   * 处理来自AudioWorklet的消息
   */
  private handleWorkletMessage(data: any): void {
    try {
      switch (data.type) {
        case 'audioData':
          // 处理录音数据
          this.handleRecordingData(
            data.audioData, 
            data.timestamp, 
            data.level, 
            data.sampleCount, 
            data.channels
          );
          break;
          
        case 'audioLevel':
          // 处理音频电平
          this.handleAudioLevel(data.level, data.timestamp);
          break;
          
        case 'testResponse':
          // 处理测试响应
          console.log('AudioManager: ✅ AudioWorklet测试响应:', data.message);
          break;
          
        default:
          console.warn('AudioManager: 未知的Worklet消息类型:', data.type);
      }
    } catch (error) {
      console.error('AudioManager: 处理Worklet消息失败:', error);
    }
  }
  
  /**
   * 处理录音数据
   */
  private handleRecordingData(audioData: ArrayBuffer, timestamp: number, level: number, sampleCount?: number, channels?: number): void {
    try {
      // 验证音频数据完整性
      if (!audioData || audioData.byteLength === 0) {
        console.warn('AudioManager: 收到空的音频数据，跳过处理');
        return;
      }
      
      // 记录音频数据统计
      if (sampleCount && channels) {
        const expectedSize = sampleCount * channels * 2; // 2 bytes per sample
        if (audioData.byteLength !== expectedSize) {
          console.warn(`AudioManager: 音频数据大小不匹配 - 期望: ${expectedSize}, 实际: ${audioData.byteLength}`);
        }
      }
      
      if (this.state.activeDeviceId && this.state.isRecording) {
        this.sendAudioToBackend(this.state.activeDeviceId, audioData);
      }
      
      // 触发音频数据事件
      this.emit('audioData', audioData);
      
    } catch (error) {
      console.error('AudioManager: 处理录音数据失败:', error);
    }
  }
  
  /**
   * 处理音频电平
   */
  private handleAudioLevel(level: number, timestamp: number): void {
    try {
      // 更新状态中的音频电平
      if (this.state.activeDeviceId) {
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [this.state.activeDeviceId]: {
              ...this.state.devices[this.state.activeDeviceId],
              audioLevel: level
            }
          }
        };
      }
      
      // 触发音频电平事件
      this.emit('audioLevel', level);
      
    } catch (error) {
      console.error('AudioManager: 处理音频电平失败:', error);
    }
  }
  
  /**
   * 显示当前音频处理模式
   */
  private logAudioProcessingMode(): void {
    const mode = this.isWorkletSupported && this.workletInitialized ? 'AudioWorklet' : 'ScriptProcessor';
    const performance = mode === 'AudioWorklet' ? '高性能' : '兼容模式';
    
    console.log('🎵 ===== 音频处理模式 =====');
    console.log(`📊 当前模式: ${mode}`);
    console.log(`⚡ 性能等级: ${performance}`);
    console.log(`🔧 支持状态: ${this.isWorkletSupported ? '支持' : '不支持'}`);
    console.log(`✅ 初始化状态: ${this.workletInitialized ? '已初始化' : '未初始化'}`);
    console.log('🎵 ========================');
  }
  
  /**
   * 测试AudioWorklet功能
   */
  async testAudioWorklet(): Promise<boolean> {
    if (!this.audioWorkletNode) {
      console.warn('AudioManager: AudioWorklet节点未初始化，无法测试');
      return false;
    }
    
    try {
      // 发送测试消息到AudioWorklet
      this.audioWorkletNode.port.postMessage({
        type: 'test',
        timestamp: Date.now()
      });
      
      console.log('AudioManager: ✅ AudioWorklet测试消息已发送');
      return true;
    } catch (error) {
      console.error('AudioManager: ❌ AudioWorklet测试失败:', error);
      return false;
    }
  }
  
  /**
   * 更新PTT状态到AudioWorklet
   */
  updatePttState(isActive: boolean): void {
    this.syncWorkletStateFromMicrophoneModule();
    if (!this.audioWorkletNode) {
      console.warn('AudioManager: AudioWorklet节点未初始化，无法更新PTT状态');
      return;
    }
    
    try {
      this.audioWorkletNode.port.postMessage({
        type: 'pttState',
        data: { isActive },
        timestamp: Date.now()
      });
      
      console.log(`AudioManager: PTT状态已更新到AudioWorklet - 激活: ${isActive}`);
    } catch (error) {
      console.error('AudioManager: 更新PTT状态到AudioWorklet失败:', error);
    }
  }

  /**
   * 根据 MQTT 下行主题更新每设备的「语音载荷」提示（仅 pcm / codec2；opus 不改变提示）。
   * 供 connect 回调在按活跃设备过滤播放之前调用，以便非活跃设备的下行仍能更新其上行策略。
   *
   * 注意：同时订阅 `…/downstream` 与 `…/downstream/c2` 时，两路都会推数据；若简单「后到覆盖」，
   * PCM 更密会让 hint 长期为 pcm，PTT 会误走 `audio/user/.../upstream`。因此一旦记过 codec2，
   * 不再被后续 pcm 报文降级（ unsubscribe / 切设备时会清掉 hint）。
   */
  recordDeviceDownstreamVoiceHint(deviceId: string, kind?: MqttAudioDownstreamKind): void {
    if (kind !== 'pcm' && kind !== 'codec2') {
      return;
    }
    const id = String(deviceId);
    if (kind === 'pcm' && this.deviceDownstreamVoiceHint.get(id) === 'codec2') {
      return;
    }
    this.deviceDownstreamVoiceHint.set(id, kind);
  }

  /** PTT 上行是否对该 deviceId 做 Codec2 编码：以最近一次 pcm/c2 下行准；无记录时回退 .env */
  private shouldUplinkUseCodec2ForDevice(deviceId: string): boolean {
    const hint = this.deviceDownstreamVoiceHint.get(String(deviceId));
    if (hint === 'codec2') {
      return true;
    }
    if (hint === 'pcm') {
      return false;
    }
    return isVoiceUplinkCodec2();
  }

  /**
   * 发送音频数据到后端
   */
  private async sendAudioToBackend(deviceId: string, audioData: ArrayBuffer): Promise<void> {
    try {
      const nowPerf = () =>
        (typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now());
      const g = globalThis as any;
      const perf = g.__WL_PTT_PERF__;
      if (perf && !perf.firstFrameLogged) {
        const now = nowPerf();
        perf.firstFrameLogged = true;
        const tClickToFirstFrame = now - (perf.clickTs || now);
        const tPttOnToFirstFrame = perf.pttOnAckTs ? now - perf.pttOnAckTs : NaN;
        const tStartRecordingToFirstFrame = perf.startRecordingTs ? now - perf.startRecordingTs : NaN;
        console.warn(
          `[PTT PERF] trace=${perf.traceId || 'unknown'} first-audio-frame ` +
            `t_click_to_firstFrame_ms=${tClickToFirstFrame.toFixed(1)} ` +
            `t_pttOn_to_firstFrame_ms=${Number.isFinite(tPttOnToFirstFrame) ? tPttOnToFirstFrame.toFixed(1) : 'n/a'} ` +
            `t_startRecording_to_firstFrame_ms=${Number.isFinite(tStartRecordingToFirstFrame) ? tStartRecordingToFirstFrame.toFixed(1) : 'n/a'}`
        );
        // 单次打点，首帧后清理，避免后续刷屏
        g.__WL_PTT_PERF__ = null;
      }

      if (this.rawMicDumpChunks && audioData.byteLength > 0) {
        this.rawMicDumpChunks.push(new Uint8Array(audioData));
      }

      if (this.shouldUplinkUseCodec2ForDevice(deviceId)) {
        // 8kHz 单声道直采：audioData 已是 8kHz mono s16le，跳过 48k→8k 重采样
        const bit = this.uplinkIs8kMono
          ? await this.codec2UplinkEncodeMono8k(audioData)
          : await this.codec2UplinkEncodeNext(audioData);
        if (!bit || bit.byteLength === 0) {
          return;
        }
        await this.enqueueCodec2UplinkBitsForMqtt(deviceId, bit);
        return;
      }

      if (audioData.byteLength % 4 !== 0) {
        console.warn('[WaveLink PCM uplink] 载荷长度非 4 的倍数（48k 立体声 s16le 应整帧对齐）', {
          deviceId,
          byteLength: audioData.byteLength,
        });
      }
      await this.publishUplinkAudioToMqtt(deviceId, audioData);
    } catch (error) {
      console.error('AudioManager: 发送音频数据到后端失败:', error);
    }
  }

  /** MQTT 发布上行音频二进制（Codec2 比特流或 PCM，取决于上游） */
  private async publishUplinkAudioToMqtt(deviceId: string, payload: ArrayBuffer): Promise<void> {
    if (!this._mqttAudioManagerMod) {
      this._mqttAudioManagerMod = await import('./MqttAudioManager');
    }
    const mqttAudioManager = this._mqttAudioManagerMod.getMqttAudioManager();
    if (!mqttAudioManager) {
      console.warn('AudioManager: MqttAudioManager 未就绪，无法上行音频');
      return;
    }
    const uplinkRoute: MqttVoiceUplinkRoute = this.shouldUplinkUseCodec2ForDevice(deviceId)
      ? 'user_codec2'
      : 'user_pcm';
    await mqttAudioManager.sendAudioToDevice(deviceId, payload, uplinkRoute);
  }

  /** 将本次 PTT 累积的 Codec2 比特流合并并触发浏览器下载 */
  private downloadCodec2Dump(): void {
    const chunks = this.codec2DumpChunks;
    this.codec2DumpChunks = null;
    if (!chunks || chunks.length === 0) {
      return;
    }
    const totalLen = chunks.reduce((s, c) => s + c.length, 0);
    const merged = new Uint8Array(totalLen);
    let offset = 0;
    for (const c of chunks) {
      merged.set(c, offset);
      offset += c.length;
    }
    const blob = new Blob([merged], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `codec2_uplink_${ts}.bin`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log(`[WaveLink] Codec2 编码数据已导出: ${totalLen} 字节, ${chunks.length} 段`);
  }

  /** 通用：合并 chunks 并触发浏览器下载 */
  private downloadDumpChunks(chunks: Uint8Array[] | null, label: string): void {
    if (!chunks || chunks.length === 0) {
      return;
    }
    const totalLen = chunks.reduce((s, c) => s + c.length, 0);
    const merged = new Uint8Array(totalLen);
    let offset = 0;
    for (const c of chunks) {
      merged.set(c, offset);
      offset += c.length;
    }
    const blob = new Blob([merged], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `${label}_${ts}.raw`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log(`[WaveLink] ${label} 已导出: ${totalLen} 字节, ${chunks.length} 段`);
  }

  private concatUint8(a: Uint8Array, b: ArrayBuffer): Uint8Array {
    const bb = new Uint8Array(b);
    if (a.length === 0) {
      return Uint8Array.from(bb);
    }
    const out = new Uint8Array(a.length + bb.length);
    out.set(a, 0);
    out.set(bb, a.length);
    return out;
  }

  /**
   * Codec2 比特流拼帧后发 MQTT：batch=1 时每段编码结果立即发送；否则缓存至满 N×frameBytes 再发，PTT 结束由 flushCodec2UplinkBitBatchTail 吐尾。
   */
  private async enqueueCodec2UplinkBitsForMqtt(deviceId: string, bit: ArrayBuffer): Promise<void> {
    if (this.codec2DumpChunks && bit.byteLength > 0) {
      this.codec2DumpChunks.push(new Uint8Array(bit));
    }
    const frameBytes = getCodec2DownstreamFrameBytes();
    const batchFrames = getCodec2UplinkMqttBatchFrameCount();
    if (batchFrames <= 1) {
      await this.publishUplinkAudioToMqtt(deviceId, bit);
      return;
    }
    const targetBytes = batchFrames * frameBytes;
    if (targetBytes < frameBytes) {
      await this.publishUplinkAudioToMqtt(deviceId, bit);
      return;
    }
    const mergedBatch = this.concatUint8(this.codec2UplinkBitBatch, bit);
    this.codec2UplinkBitBatch = new Uint8Array(mergedBatch);
    while (this.codec2UplinkBitBatch.length >= targetBytes) {
      const chunk = this.codec2UplinkBitBatch.subarray(0, targetBytes);
      const buf = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
      await this.publishUplinkAudioToMqtt(deviceId, buf);
      this.codec2UplinkBitBatch = Uint8Array.from(this.codec2UplinkBitBatch.subarray(targetBytes));
    }
  }

  /** PTT 结束或切设备：发出缓冲区内所有完整 Codec2 帧（最后不足 frameBytes 的尾字节丢弃并告警） */
  private async flushCodec2UplinkBitBatchTail(deviceId: string): Promise<void> {
    const frameBytes = getCodec2DownstreamFrameBytes();
    const n = this.codec2UplinkBitBatch.length;
    if (n === 0) {
      return;
    }
    const complete = Math.floor(n / frameBytes) * frameBytes;
    if (complete <= 0) {
      console.warn('[WaveLink Codec2 uplink] 聚帧缓冲尾非整帧，丢弃', {
        deviceId,
        strayBytes: n,
        frameBytes,
      });
      this.codec2UplinkBitBatch = new Uint8Array(0);
      return;
    }
    const chunk = this.codec2UplinkBitBatch.subarray(0, complete);
    const buf = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
    await this.publishUplinkAudioToMqtt(deviceId, buf);
    this.codec2UplinkBitBatch = new Uint8Array(0);
  }

  /**
   * 8kHz mono s16le 直接送入 Codec2 编码（跳过 48k→8k 重采样），逻辑与 codec2UplinkEncodeNext 对称。
   */
  private async codec2UplinkEncodeMono8k(mono8kPcm: ArrayBuffer): Promise<ArrayBuffer | null> {
    if (mono8kPcm.byteLength === 0) return null;
    const merged = this.concatUint8(this.codec2Uplink8kRemainder, mono8kPcm);
    this.codec2Uplink8kRemainder = new Uint8Array(merged);
    const frameB = getCodec2UplinkMonoPcmBytesPerFrame();
    const fullFrames = Math.floor(this.codec2Uplink8kRemainder.length / frameB);
    if (fullFrames === 0) return null;
    const take = fullFrames * frameB;
    const slice = this.codec2Uplink8kRemainder.subarray(0, take);
    const rest = take >= this.codec2Uplink8kRemainder.length
      ? new Uint8Array(0)
      : this.codec2Uplink8kRemainder.subarray(take);
    this.codec2Uplink8kRemainder = Uint8Array.from(rest);
    const pcmBuf = slice.buffer.slice(slice.byteOffset, slice.byteOffset + slice.byteLength) as ArrayBuffer;
    const enc = await getCodec2EncodeClient().encode(getCodec2Mode(), pcmBuf);
    if (!enc.ok) {
      console.warn('[WaveLink Codec2 8k direct] 编码失败', enc.error);
      return null;
    }
    return enc.bit;
  }

  /**
   * 48k 立体声 → 8k mono；余量内所有完整 PCM 帧（每帧 320B 等）一次性送入 c2enc（批量编码，单次 Worker），
   * 返回整段 Codec2 比特流；MQTT 侧再由 enqueueCodec2UplinkBitsForMqtt 按 N 帧（默认 11）拼包发送。
   */
  private async codec2UplinkEncodeNext(stereo48Pcm: ArrayBuffer): Promise<ArrayBuffer | null> {
    const mono8k = pcmStereo48kToMono8kS16le(stereo48Pcm);
    if (mono8k.byteLength === 0) {
      return null;
    }
    const merged = this.concatUint8(this.codec2Uplink8kRemainder, mono8k);
    this.codec2Uplink8kRemainder = new Uint8Array(merged);
    const frameB = getCodec2UplinkMonoPcmBytesPerFrame();
    const fullFrames = Math.floor(this.codec2Uplink8kRemainder.length / frameB);
    if (fullFrames === 0) {
      return null;
    }
    const take = fullFrames * frameB;
    const slice = this.codec2Uplink8kRemainder.subarray(0, take);
    const rest =
      take >= this.codec2Uplink8kRemainder.length
        ? new Uint8Array(0)
        : this.codec2Uplink8kRemainder.subarray(take);
    this.codec2Uplink8kRemainder = Uint8Array.from(rest);

    const pcmBuf = slice.buffer.slice(slice.byteOffset, slice.byteOffset + slice.byteLength);
    const enc = await getCodec2EncodeClient().encode(getCodec2Mode(), pcmBuf);
    if (!enc.ok) {
      console.warn('[WaveLink Codec2 uplink] 编码失败', enc.error);
      return null;
    }
    const outBit = enc.bit;
    const fb = getCodec2DownstreamFrameBytes();
    if (outBit.byteLength % fb !== 0) {
      console.warn('[WaveLink Codec2 uplink] 编码输出长度非帧长的整数倍（聚帧/设备切分可能异常）', {
        byteLength: outBit.byteLength,
        frameBytes: fb,
        mode: getCodec2Mode(),
        pcmInputBytes: pcmBuf.byteLength,
      });
    }
    return outBit;
  }

  /** PTT 结束：尾字节补零凑整帧后发最后一包 Codec2（可选），并刷 Codec2 聚帧缓冲 */
  private async flushCodec2UplinkRemainder(): Promise<void> {
    const id = this.state.activeDeviceId;
    try {
      if (!id || !this.shouldUplinkUseCodec2ForDevice(id)) {
        this.codec2Uplink8kRemainder = new Uint8Array(0);
        return;
      }
      if (this.codec2Uplink8kRemainder.length === 0) {
        return;
      }
      const padded = padUplinkPcmToCodec2FrameMultiple(
        this.codec2Uplink8kRemainder.buffer.slice(
          this.codec2Uplink8kRemainder.byteOffset,
          this.codec2Uplink8kRemainder.byteOffset + this.codec2Uplink8kRemainder.byteLength
        )
      );
      this.codec2Uplink8kRemainder = new Uint8Array(0);
      const enc = await getCodec2EncodeClient().encode(getCodec2Mode(), padded);
      if (!enc.ok) {
        console.warn('[WaveLink Codec2 uplink] 尾包编码失败', enc.error);
        return;
      }
      const tailBit = enc.bit;
      const fb = getCodec2DownstreamFrameBytes();
      if (tailBit.byteLength % fb !== 0) {
        console.warn('[WaveLink Codec2 uplink] 尾包编码输出非整帧对齐', {
          byteLength: tailBit.byteLength,
          frameBytes: fb,
        });
      }
      await this.enqueueCodec2UplinkBitsForMqtt(id, tailBit);
    } catch (e) {
      console.error('AudioManager: Codec2 尾包处理失败', e);
    } finally {
      if (id && this.shouldUplinkUseCodec2ForDevice(id)) {
        try {
          await this.flushCodec2UplinkBitBatchTail(id);
        } catch (e2) {
          console.error('AudioManager: Codec2 聚帧尾包上行失败', e2);
        }
      } else {
        this.codec2UplinkBitBatch = new Uint8Array(0);
      }
    }
  }
  
  /**
   * 连接到MQTT音频系统
   */
  async connect(): Promise<void> {
    try {
      if (!this.state.isInitialized || !this.microphoneModule) {
        throw new Error('音频管理器未初始化，无法连接MQTT音频系统');
      }

      this.deviceStatusMqttUnsub?.();
      this.deviceStatusMqttUnsub = mqttService.addDeviceStatusListener((deviceId: string, status: any) => {
        console.log(`AudioManager: 收到设备状态更新 - 设备ID: ${deviceId}`, status);
        this.emit('deviceStatusChanged', { deviceId, status });

        let statusObj = status;
        if (typeof status === 'string') {
          try {
            statusObj = JSON.parse(status);
          } catch (e) {
            console.warn('AudioManager: 无法解析状态字符串:', status);
            return;
          }
        }

        if (
          statusObj &&
          (statusObj.vfoAFrequency !== undefined ||
            statusObj.vfoBFrequency !== undefined ||
            (statusObj.data &&
              (statusObj.data.vfoAFrequency !== undefined || statusObj.data.vfoBFrequency !== undefined)))
        ) {
          const frequencyStatus = statusObj.data || statusObj;

          const fullStatus = {
            deviceId: statusObj.deviceId || deviceId,
            vfoAFrequency: frequencyStatus.vfoAFrequency || 0,
            vfoBFrequency: frequencyStatus.vfoBFrequency || 0,
            vfoAMode: frequencyStatus.vfoAMode || 'Unknown',
            vfoBMode: frequencyStatus.vfoBMode || 'Unknown',
            isOnline: frequencyStatus.isOnline !== undefined ? frequencyStatus.isOnline : true,
            deviceIp: frequencyStatus.deviceIp || '',
            timestamp: frequencyStatus.timestamp || Date.now()
          };

          console.log(`AudioManager: 触发设备频率状态更新 - 设备ID: ${fullStatus.deviceId}`, fullStatus);

          this.emit('deviceFrequencyStatusUpdate', fullStatus);

          if (
            String(this.activeDeviceId) === String(deviceId) ||
            String(this.activeDeviceId) === String(fullStatus.deviceId)
          ) {
            this.emit('activeDeviceFrequencyStatusUpdate', fullStatus);
          }
        }
      });

      // 设置MQTT服务回调（设备状态走 addDeviceStatusListener，避免与 RealtimeService 的 setCallbacks 互相覆盖）
      mqttService.setCallbacks({
        onConnect: () => {
          this.state.isConnected = true;
          this.emit('connected');
        },
        onDisconnect: () => {
          this.state.isConnected = false;
          this.emit('disconnected');
        },
        onAudioReceived: (deviceId: string, audioData: ArrayBuffer, downstreamKind?: MqttAudioDownstreamKind) => {
          // 收到音频数据，传递给播放逻辑
          // 高频日志关闭：下行每包都会触发
          // console.log(`🎵 AudioManager: 收到设备音频数据 - 设备ID: ${deviceId}, 数据大小: ${audioData.byteLength}字节`);
          
          // 重置音频数据接收超时定时器
          this.resetAudioDataReceivedTimeout();

          this.recordDeviceDownstreamVoiceHint(deviceId, downstreamKind);
          
          // 检查是否是当前活跃设备
          // 注意：deviceId可能是字符串或数字，需要统一比较
          const activeDeviceIdStr = this.activeDeviceId ? String(this.activeDeviceId) : null;
          const receivedDeviceIdStr = String(deviceId);
          
          if (activeDeviceIdStr && activeDeviceIdStr === receivedDeviceIdStr) {
            // console.log(`🎵 AudioManager: 收到活跃设备音频数据，开始播放 - 设备ID: ${deviceId}, 数据大小: ${audioData.byteLength}字节`);
            this.playReceivedAudio(deviceId, audioData, downstreamKind);
          } else {
            console.warn('[WaveLink Codec2 downstream] skip non-active device audio', {
              receivedDeviceId: receivedDeviceIdStr,
              activeDeviceId: activeDeviceIdStr,
              byteLength: audioData.byteLength,
            });
          }
        },
        onError: (error: Error) => {
          console.error('AudioManager: MQTT错误:', error);
          this.emit('error', error);
        }
      });
      
      // 连接到MQTT服务（如果未初始化会自动初始化）
      await mqttService.connect();
      
      console.log('AudioManager: MQTT连接成功');
    } catch (error) {
      console.error('AudioManager: MQTT音频系统连接失败:', error);
      throw error;
    }
  }
  
  /**
   * 请求麦克风权限
   */
  async requestMicrophonePermission(): Promise<void> {
    if (!this.state.isInitialized || !this.microphoneModule) {
      throw new Error('音频管理器未初始化');
    }
    
    try {
      await this.microphoneModule.requestPermission();
      this.state.microphonePermission = 'granted';
      this.emit('microphonePermissionChanged', 'granted');
      console.log('AudioManager: 麦克风权限已获取');
    } catch (error) {
      this.state.microphonePermission = 'denied';
      this.emit('microphonePermissionChanged', 'denied');
      console.error('AudioManager: 麦克风权限获取失败:', error);
      throw error;
    }
  }
  
  /**
   * 订阅设备音频（带重试机制）
   */
  private async subscribeToDeviceAudioWithRetry(deviceId: string, maxRetries: number = 3): Promise<boolean> {
    console.log(`AudioManager: [订阅重试] subscribeToDeviceAudioWithRetry 开始 - 设备ID: ${deviceId}, 最大重试次数: ${maxRetries}`);
    
    const { mqttService } = await import('../mqttService');
    console.log(`AudioManager: [订阅重试] mqttService 导入完成`);
    console.log(`AudioManager: [订阅重试] mqttService.isServiceInitialized(): ${mqttService.isServiceInitialized()}`);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`AudioManager: [订阅重试] 尝试订阅设备音频 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}`);
        
        // 确保MQTT连接已建立
        if (!mqttService.isServiceInitialized()) {
          console.log(`AudioManager: [订阅重试] MQTT服务未初始化，开始初始化...`);
          await mqttService.connect();
          console.log(`AudioManager: [订阅重试] MQTT服务初始化完成`);
        } else {
          console.log(`AudioManager: [订阅重试] MQTT服务已初始化`);
        }
        
        // 订阅设备音频
        console.log(`AudioManager: [订阅重试] 调用 mqttService.subscribeToDeviceAudio(${deviceId})...`);
        await mqttService.subscribeToDeviceAudio(deviceId);
        console.log(`AudioManager: [订阅重试] mqttService.subscribeToDeviceAudio() 调用完成`);
        
        // 验证订阅是否成功
        await new Promise(resolve => setTimeout(resolve, 500)); // 等待订阅完成
        const verified = await mqttService.verifyDeviceAudioSubscription(deviceId);
        
        if (verified) {
          console.log(`✅ AudioManager: 设备音频订阅成功并已验证 - 设备ID: ${deviceId}`);
          return true;
        } else {
          console.warn(`⚠️ AudioManager: 订阅验证失败 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}`);
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // 递增延迟重试
          }
        }
      } catch (error) {
        console.error(`❌ AudioManager: 订阅设备音频失败 - 设备ID: ${deviceId}, 尝试 ${attempt}/${maxRetries}:`, error);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // 递增延迟重试
        } else {
          throw error;
        }
      }
    }
    
    console.error(`❌ AudioManager: 设备音频订阅失败（已重试${maxRetries}次） - 设备ID: ${deviceId}`);
    return false;
  }

  /**
   * 重置音频数据接收超时定时器
   */
  private resetAudioDataReceivedTimeout(): void {
    if (this.audioDataReceivedTimeout) {
      clearTimeout(this.audioDataReceivedTimeout);
    }
    
    if (this.activeDeviceId && this.state.isListening) {
      this.audioDataReceivedTimeout = setTimeout(() => {
        console.warn(`⚠️ AudioManager: 长时间未收到音频数据（${this.AUDIO_DATA_TIMEOUT_MS}ms），请检查MQTT订阅状态 - 设备ID: ${this.activeDeviceId}`);
        // 可以在这里触发一个事件，让UI显示警告
        this.emit('error', new Error(`长时间未收到音频数据，请检查MQTT订阅状态`));
      }, this.AUDIO_DATA_TIMEOUT_MS);
    }
  }
  
  /**
   * 开始监听设备
   */
  async startListeningDevice(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: startListeningDevice 开始 - 设备ID: ${deviceId}`);
      
      // 向后端注册用户监听设备（后端会自动管理后端MQTT订阅）
      await this.registerUserListeningDevice(deviceId);
      console.log(`AudioManager: 后端注册完成 - 设备ID: ${deviceId}`);
      
      // 前端也需要订阅MQTT主题以接收音频数据（带重试机制）
      console.log(`AudioManager: 开始前端MQTT订阅 - 设备ID: ${deviceId}`);
      const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
      if (!subscriptionSuccess) {
        console.error(`❌ AudioManager: 前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
        throw new Error(`前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
      }
      console.log(`AudioManager: 前端MQTT订阅成功 - 设备ID: ${deviceId}`);
      
      // 启动音频数据接收监控
      console.log(`AudioManager: 启动音频数据接收监控 - 设备ID: ${deviceId}`);
      this.resetAudioDataReceivedTimeout();
      
      // 更新系统状态
      console.log(`AudioManager: 设置活跃设备ID为 ${deviceId}`);
      this.activeDeviceId = deviceId; // 修复：同时更新实例变量
      this.state = {
        ...this.state,
        activeDeviceId: deviceId,
        isListening: true,
        devices: {
          ...this.state.devices,
          [deviceId]: {
            ...this.state.devices[deviceId],
            isActive: true,
            isPlaying: true
          }
        }
      };
      
      // 确保连续播放节点已初始化（如果支持AudioWorklet）
      if (this.audioContext && this.isWorkletSupported && !this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
        console.log(`AudioManager: 初始化连续播放节点 - 设备ID: ${deviceId}`);
        this.initializeContinuousPlayback().catch(error => {
          console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
        });
      }
      
      console.log(`AudioManager: 开始监听设备 ${deviceId}, 设置后的活跃设备ID: ${this.activeDeviceId}`);
    } catch (error) {
      console.error(`AudioManager: 开始监听设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 向后端注册用户监听设备
   */
  private async registerUserListeningDevice(deviceId: string): Promise<void> {
    try {
      // 动态导入ApiClient避免循环依赖
      const { ApiClient } = await import('../../utils/apiClient');
      
      // 使用新的连接管理API
      // 注意：getBaseUrl()已经包含了/api，所以这里不需要再加/api
      const response = await ApiClient.post<any>('/connections/listen', {
        deviceId: parseInt(deviceId, 10) // 转换为数字类型
      });
      
      if (response.success) {
        console.log(`AudioManager: 用户监听设备注册成功 - ${deviceId}`);
      } else {
        console.error(`AudioManager: 用户监听设备注册失败 - ${deviceId}:`, response.message);
        throw new Error(`用户监听设备注册失败: ${response.message}`);
      }
    } catch (error) {
      console.error(`AudioManager: 用户监听设备注册异常 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 处理设备频率状态更新
   */
  private handleDeviceFrequencyStatusUpdate(parsedStatus: any): void {
    try {
      // 处理设备频率状态更新
      
      // 触发设备频率状态更新事件
      this.emit('deviceFrequencyStatusUpdate', parsedStatus);
      
      // 如果当前正在监听这个设备，更新状态
      if (this.activeDeviceId === parsedStatus.deviceId) {
        this.emit('activeDeviceFrequencyStatusUpdate', parsedStatus);
      }
      
    } catch (error) {
      console.error('AudioManager: 处理设备频率状态更新失败:', error);
    }
  }
  
  /**
   * 切换目标设备
   */
  async switchTargetDevice(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: 开始切换设备到 ${deviceId}, 当前活跃设备: ${this.state.activeDeviceId}`);
      
      // 如果当前设备与新设备相同，确保订阅和音频播放状态正确
      // 注意：需要同时检查字符串和数字类型的比较，因为deviceId可能是字符串或数字
      const currentDeviceId = this.state.activeDeviceId;
      if (currentDeviceId === deviceId || String(currentDeviceId) === String(deviceId)) {
        console.log(`AudioManager: 设备已经是当前活跃设备，确保订阅和音频播放状态: ${deviceId} (当前设备: ${currentDeviceId})`);
        // 即使设备相同，也确保MQTT订阅、音频上下文和播放节点状态正确
        await this.ensureDeviceSubscribed(deviceId);
        return;
      }
      
      // 使用新的切换API，后端会自动处理停止旧设备和开始新设备
      const { ApiClient } = await import('../../utils/apiClient');
      
      // 注意：getBaseUrl()已经包含了/api，所以这里不需要再加/api
      const response = await ApiClient.post<any>('/connections/listen/switch', {
        deviceId: parseInt(deviceId, 10) // 转换为数字类型
      });
      
      console.log(`AudioManager: [DEBUG] 收到后端响应:`, response);
      console.log(`AudioManager: [DEBUG] response.success:`, response?.success);
      console.log(`AudioManager: [DEBUG] response类型:`, typeof response);
      
      if (response && response.success) {
        console.log(`AudioManager: [DEBUG] 进入if块 - 切换设备成功 - ${deviceId}`);
        console.log(`AudioManager: 切换设备成功 - ${deviceId}`);
        console.log(`AudioManager: 响应数据:`, response);
        
        const oldDeviceId = this.state.activeDeviceId;
        
        // 【修复1】清理旧设备的资源
        if (oldDeviceId && String(oldDeviceId) !== String(deviceId)) {
          console.log(`AudioManager: [切换] 开始清理旧设备资源 - 设备ID: ${oldDeviceId}`);
          
          // 清理旧设备的音频缓冲区
          if (this.audioBufferQueue.has(oldDeviceId)) {
            this.audioBufferQueue.delete(oldDeviceId);
            console.log(`AudioManager: [切换] 已清理旧设备音频缓冲区 - 设备ID: ${oldDeviceId}`);
          }
          
          // 清理旧设备的网络监控数据
          this.networkLatency.delete(oldDeviceId);
          this.packetLossRate.delete(oldDeviceId);
          this.lastPacketTime.delete(oldDeviceId);
          console.log(`AudioManager: [切换] 已清理旧设备网络监控数据 - 设备ID: ${oldDeviceId}`);
          
          // 清理旧设备的播放状态
          this.isPlaying.delete(oldDeviceId);
          this.lastPlayTime.delete(oldDeviceId);
          this.clearCodec2DownstreamState(String(oldDeviceId));
          this.deviceDownstreamVoiceHint.delete(String(oldDeviceId));
          this.codec2Uplink8kRemainder = new Uint8Array(0);
          this.codec2UplinkBitBatch = new Uint8Array(0);
          console.log(`AudioManager: [切换] 已清理旧设备播放状态 - 设备ID: ${oldDeviceId}`);
        }
        
        // 【修复2】提前清空播放节点缓冲区，避免播放旧设备的音频片段
        if (this.continuousPlaybackNode) {
          try {
            console.log(`AudioManager: [切换] 清空播放节点缓冲区 - 旧设备ID: ${oldDeviceId}, 新设备ID: ${deviceId}`);
            this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
            console.log(`AudioManager: [切换] 播放节点缓冲区已清空`);
          } catch (error) {
            console.warn(`AudioManager: [切换] 清空播放节点缓冲区失败:`, error);
          }
        }
        if (this.codec2ContinuousPlaybackNode) {
          try {
            this.codec2ContinuousPlaybackNode.port.postMessage({ type: 'clear' });
          } catch (error) {
            console.warn(`AudioManager: [切换] 清空 Codec2 8k 播放环缓失败:`, error);
          }
        }
        
        // 【修复2】等待后端事件处理完成（后端@Async事件监听器需要时间处理订阅/取消订阅）
        // 使用轮询验证方式，而不是固定等待时间，更可靠
        console.log(`AudioManager: [修复] 等待后端事件处理完成（轮询验证）...`);
        
        // 提前导入mqttService，避免重复导入
        const { mqttService } = await import('../mqttService');
        console.log(`AudioManager: [修复] MQTT服务导入成功`);
        console.log(`AudioManager: [修复] MQTT服务初始化状态: ${mqttService.isServiceInitialized()}`);
        
        // 先等待一个基础时间，让后端事件开始处理
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 然后轮询验证订阅状态，最多等待500ms（5次，每次100ms）
        const maxPollAttempts = 5;
        const pollInterval = 100;
        let backendSubscribed = false;
        
        for (let attempt = 0; attempt < maxPollAttempts; attempt++) {
          try {
            const isSubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
            if (isSubscribed) {
              backendSubscribed = true;
              console.log(`AudioManager: [修复] 后端订阅验证成功（第${attempt + 1}次尝试） - 设备ID: ${deviceId}`);
              break;
            }
          } catch (error) {
            console.debug(`AudioManager: [修复] 订阅验证失败（第${attempt + 1}次尝试）:`, error);
          }
          
          if (attempt < maxPollAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
          }
        }
        
        if (!backendSubscribed) {
          console.log(`AudioManager: [修复] 后端订阅验证超时，继续执行前端订阅流程 - 设备ID: ${deviceId}`);
        }
        
        // 更新activeDeviceId（在清空缓冲区和等待后端事件之后）
        this.activeDeviceId = deviceId;
        console.log(`AudioManager: 已更新activeDeviceId - 从 ${oldDeviceId} 到 ${deviceId}`);
        
        // 【修复3】前端验证并补充订阅（后端可能已经订阅，但前端需要确保订阅状态正确）
        console.log(`AudioManager: [订阅检查] 开始前端MQTT订阅验证流程 - 设备ID: ${deviceId}`);
        console.log(`AudioManager: [订阅检查] 当前状态 - activeDeviceId: ${this.activeDeviceId}, isInitialized: ${this.state.isInitialized}`);
        
        try {
          
          // 取消旧设备的订阅（如果存在且与新设备不同）
          // 注意：后端已经通过ConnectionEventListener处理了取消订阅，这里作为补充确保
          if (oldDeviceId && oldDeviceId !== deviceId && String(oldDeviceId) !== String(deviceId)) {
            try {
              console.log(`AudioManager: [补充] 取消旧设备订阅 - 设备ID: ${oldDeviceId}`);
              await mqttService.unsubscribeFromDeviceAudio(oldDeviceId);
              console.log(`AudioManager: [补充] 前端取消订阅旧设备成功 - 设备ID: ${oldDeviceId}`);
            } catch (unsubError) {
              // 如果后端已经取消订阅，这里可能会失败，但不影响流程
              console.debug(`AudioManager: [补充] 取消旧设备订阅（可能后端已处理） - 设备ID: ${oldDeviceId}`);
            }
          }
          
          // 【修复4】先验证订阅状态，如果已订阅则跳过，避免重复订阅
          console.log(`AudioManager: [修复] 验证新设备订阅状态 - 设备ID: ${deviceId}`);
          const isAlreadySubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
          
          if (!isAlreadySubscribed) {
            // 订阅新设备（带重试机制）
            console.log(`AudioManager: [修复] 设备未订阅，开始订阅新设备音频 - 设备ID: ${deviceId}`);
            const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
            if (!subscriptionSuccess) {
              console.error(`❌ AudioManager: 前端MQTT订阅失败（已重试3次） - 设备ID: ${deviceId}`);
              // 即使订阅失败，也继续执行，但会触发音频数据接收监控警告
            } else {
              console.log(`✅ AudioManager: 前端MQTT订阅流程完成 - 设备ID: ${deviceId}`);
              
              // 【修复5】订阅后再次验证，确保订阅成功
              const verifyAfterSubscribe = await mqttService.verifyDeviceAudioSubscription(deviceId);
              if (!verifyAfterSubscribe) {
                console.warn(`⚠️ AudioManager: 订阅后验证失败，但继续执行 - 设备ID: ${deviceId}`);
              } else {
                console.log(`✅ AudioManager: 订阅后验证成功 - 设备ID: ${deviceId}`);
              }
            }
          } else {
            console.log(`✅ AudioManager: 设备已订阅（后端已处理），跳过前端订阅 - 设备ID: ${deviceId}`);
          }
        } catch (error) {
          console.error(`❌ AudioManager: 前端MQTT订阅流程异常 - 设备ID: ${deviceId}:`, error);
          // 继续执行，不阻塞设备切换
        }
        
        // 启动音频数据接收监控
        console.log(`AudioManager: 启动音频数据接收监控 - 设备ID: ${deviceId}`);
        this.resetAudioDataReceivedTimeout();
        
        // 更新系统状态
        this.state = {
          ...this.state,
          activeDeviceId: deviceId,
          isListening: true,
          devices: {
            ...this.state.devices,
            // 更新旧设备状态
            ...(oldDeviceId && String(oldDeviceId) !== String(deviceId) && {
              [oldDeviceId]: {
                ...this.state.devices[oldDeviceId],
                isActive: false,
                isPlaying: false
              }
            }),
            // 更新新设备状态
            [deviceId]: {
              ...this.state.devices[deviceId],
              isActive: true,
              isPlaying: true
            }
          }
        };
        
        // 触发设备切换事件
        this.emit('activeDeviceChanged', { 
          deviceId, 
          previousDeviceId: oldDeviceId 
        });
        
        // 确保音频上下文已恢复（设备切换后可能需要重新激活）
        try {
          if (this.audioContext && this.audioContext.state === 'suspended') {
            console.log(`AudioManager: 设备切换后恢复音频上下文 - 设备ID: ${deviceId}`);
            await this.audioContext.resume();
            console.log(`AudioManager: 音频上下文已恢复 - 状态: ${this.audioContext.state}`);
          } else if (!this.audioContext) {
            console.log(`AudioManager: 设备切换后创建音频上下文 - 设备ID: ${deviceId}`);
            await this.createAudioContext();
          }
          
          // 确保连续播放节点已初始化（设备切换后需要确保播放节点可用）
          if (this.audioContext && this.isWorkletSupported) {
            if (!this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
              console.log(`AudioManager: 设备切换后初始化连续播放节点 - 设备ID: ${deviceId}`);
              try {
                await this.initializeContinuousPlayback();
                console.log(`AudioManager: 连续播放节点初始化成功 - 设备ID: ${deviceId}`);
              } catch (error) {
                console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
              }
            } else if (this.continuousPlaybackNode) {
              // 播放节点已存在，确保已正确连接（缓冲区已在前面清空）
              console.log(`AudioManager: 设备切换后确保播放节点连接 - 设备ID: ${deviceId}`);
              try {
                // 重新连接播放节点到音频上下文（确保音频流正确更新）
                if (this.audioContext && this.audioContext.state === 'running') {
                  try {
                    // 先断开连接（如果已连接）
                    this.continuousPlaybackNode.disconnect();
                    // 然后重新连接到destination（确保音频流更新）
                    this.continuousPlaybackNode.connect(this.audioContext.destination);
                    console.log(`AudioManager: 播放节点已重新连接到音频上下文 - 设备ID: ${deviceId}`);
                  } catch (connectError) {
                    console.warn(`AudioManager: 重新连接播放节点失败 - 设备ID: ${deviceId}:`, connectError);
                  }
                }
              } catch (error) {
                console.warn(`AudioManager: 处理播放节点连接失败 - 设备ID: ${deviceId}:`, error);
              }
            }
          }
        } catch (error) {
          console.warn(`AudioManager: 设备切换后音频上下文处理失败 - 设备ID: ${deviceId}:`, error);
        }
      
        console.log(`AudioManager: 切换设备完成 ${deviceId}, 当前活跃设备: ${this.activeDeviceId}`);
      } else {
        throw new Error(`切换设备失败: ${response.message}`);
      }
    } catch (error) {
      console.error(`AudioManager: 切换目标设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 确保设备已订阅MQTT主题（即使设备没有变化）
   * 用于页面切换回来时确保订阅状态正确
   * 同时确保音频上下文、播放节点和播放状态正确
   */
  async ensureDeviceSubscribed(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: 确保设备订阅和音频播放状态 - 设备ID: ${deviceId}`);
      
      // 1. 确保MQTT订阅
      const { mqttService } = await import('../mqttService');
      const isSubscribed = await mqttService.verifyDeviceAudioSubscription(deviceId);
      
      if (!isSubscribed) {
        console.log(`AudioManager: 设备未订阅，开始订阅 - 设备ID: ${deviceId}`);
        const subscriptionSuccess = await this.subscribeToDeviceAudioWithRetry(deviceId);
        if (!subscriptionSuccess) {
          console.warn(`AudioManager: 设备订阅失败 - 设备ID: ${deviceId}`);
        } else {
          console.log(`AudioManager: 设备订阅成功 - 设备ID: ${deviceId}`);
        }
      } else {
        console.log(`AudioManager: 设备已订阅 - 设备ID: ${deviceId}`);
      }
      
      // 2. 确保activeDeviceId正确设置
      if (this.activeDeviceId !== deviceId && String(this.activeDeviceId) !== String(deviceId)) {
        console.log(`AudioManager: 更新活跃设备ID - 从 ${this.activeDeviceId} 到 ${deviceId}`);
        this.activeDeviceId = deviceId;
        this.state = {
          ...this.state,
          activeDeviceId: deviceId,
          isListening: true,
          devices: {
            ...this.state.devices,
            [deviceId]: {
              ...this.state.devices[deviceId],
              isActive: true,
              isPlaying: true
            }
          }
        };
      }
      
      // 3. 确保音频上下文已恢复/创建
      try {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          console.log(`AudioManager: 恢复音频上下文 - 设备ID: ${deviceId}`);
          await this.audioContext.resume();
          console.log(`AudioManager: 音频上下文已恢复 - 状态: ${this.audioContext.state}`);
        } else if (!this.audioContext) {
          console.log(`AudioManager: 创建音频上下文 - 设备ID: ${deviceId}`);
          await this.createAudioContext();
        }
      } catch (error) {
        console.warn(`AudioManager: 音频上下文处理失败 - 设备ID: ${deviceId}:`, error);
      }
      
      // 4. 确保连续播放节点已初始化或重置
      if (this.audioContext && this.isWorkletSupported) {
        if (!this.continuousPlaybackNode && !this.isInitializingAudioWorklet) {
          console.log(`AudioManager: 初始化连续播放节点 - 设备ID: ${deviceId}`);
          try {
            await this.initializeContinuousPlayback();
            console.log(`AudioManager: 连续播放节点初始化成功 - 设备ID: ${deviceId}`);
          } catch (error) {
            console.warn(`AudioManager: 连续播放节点初始化失败 - 设备ID: ${deviceId}:`, error);
          }
        } else if (this.continuousPlaybackNode) {
          // 播放节点已存在，清空缓冲区（确保新设备的音频数据能正确播放）
          console.log(`AudioManager: 清空播放节点缓冲区并重新连接 - 设备ID: ${deviceId}`);
          try {
            // 清空缓冲区
            this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
            if (this.codec2ContinuousPlaybackNode) {
              this.codec2ContinuousPlaybackNode.port.postMessage({ type: 'clear' });
            }
            console.log(`AudioManager: 播放节点缓冲区已清空 - 设备ID: ${deviceId}`);
            
            // 重新连接播放节点到音频上下文（确保音频流正确更新）
            if (this.audioContext && this.audioContext.state === 'running') {
              try {
                // 先断开连接（如果已连接）
                this.continuousPlaybackNode.disconnect();
                // 然后重新连接到destination（确保音频流更新）
                this.continuousPlaybackNode.connect(this.audioContext.destination);
                console.log(`AudioManager: 播放节点已重新连接到音频上下文 - 设备ID: ${deviceId}`);
              } catch (connectError) {
                console.warn(`AudioManager: 重新连接播放节点失败 - 设备ID: ${deviceId}:`, connectError);
              }
            }
          } catch (error) {
            console.warn(`AudioManager: 清空播放节点缓冲区失败 - 设备ID: ${deviceId}:`, error);
          }
        }
      }
      
      // 5. 启动音频数据接收监控
      this.resetAudioDataReceivedTimeout();
      
      console.log(`AudioManager: 设备订阅和音频播放状态确保完成 - 设备ID: ${deviceId}`);
    } catch (error) {
      console.error(`AudioManager: 确保设备订阅失败 - 设备ID: ${deviceId}:`, error);
    }
  }
  
  /**
   * 停止监听设备
   */
  async stopListeningDevice(deviceId: string): Promise<void> {
    try {
      const wasActive =
        this.activeDeviceId !== null && String(this.activeDeviceId) === String(deviceId);

      // 先向后端取消注册用户监听设备（在token失效前）
      await this.unregisterUserListeningDevice(deviceId);
      
      // 前端也需要取消订阅MQTT主题
      try {
        const { mqttService } = await import('../mqttService');
      await mqttService.unsubscribeFromDeviceAudio(deviceId);
        console.log(`AudioManager: 前端取消订阅成功 - 设备ID: ${deviceId}`);
      } catch (mqttError) {
        console.warn(`AudioManager: 前端取消订阅失败（不影响后端取消订阅） - 设备ID: ${deviceId}:`, mqttError);
        // 前端取消订阅失败不影响后端取消订阅，继续执行
      }
      
      // 更新系统状态
      this.state = {
        ...this.state,
        devices: {
          ...this.state.devices,
          [deviceId]: {
            ...this.state.devices[deviceId],
            isActive: false,
            isPlaying: false
          }
        }
      };
      
      // 如果这是当前活跃设备，清除活跃设备
      if (this.state.activeDeviceId === deviceId) {
        console.log(`AudioManager: 停止当前活跃设备 ${deviceId}，清除活跃设备ID`);
        this.activeDeviceId = null; // 修复：同时清除实例变量
        this.state = {
          ...this.state,
          activeDeviceId: null,
          isListening: false
        };
        
        // 清除音频数据接收监控
        if (this.audioDataReceivedTimeout) {
          clearTimeout(this.audioDataReceivedTimeout);
          this.audioDataReceivedTimeout = null;
        }
        
        console.log(`AudioManager: 停止设备后，活跃设备ID: ${this.activeDeviceId}`);
      } else {
        console.log(`AudioManager: 停止非活跃设备 ${deviceId}，当前活跃设备: ${this.activeDeviceId}`);
      }
      
      this.clearCodec2DownstreamState(String(deviceId));
      this.deviceDownstreamVoiceHint.delete(String(deviceId));
      if (wasActive) {
        this.codec2Uplink8kRemainder = new Uint8Array(0);
        this.codec2UplinkBitBatch = new Uint8Array(0);
      }
      console.log(`AudioManager: 停止监听设备 ${deviceId}`);
    } catch (error) {
      console.error(`AudioManager: 停止监听设备失败 - ${deviceId}:`, error);
      throw error;
    }
  }
  
  /**
   * 向后端取消注册用户监听设备
   */
  private async unregisterUserListeningDevice(deviceId: string): Promise<void> {
    try {
      console.log(`AudioManager: 开始取消注册用户监听设备 - ${deviceId}`);
      // 动态导入ApiClient避免循环依赖
      const { ApiClient } = await import('../../utils/apiClient');
      
      // 使用新的连接管理API，将deviceId作为查询参数
      // 注意：getBaseUrl()已经包含了/api，所以这里不需要再加/api
      const deviceIdNum = parseInt(deviceId, 10);
      const response = await ApiClient.post<any>(`/connections/listen/stop?deviceId=${deviceIdNum}`, null);
      
      if (response.success) {
        console.log(`AudioManager: 用户监听设备取消注册成功 - ${deviceId}`);
      } else {
        console.warn(`AudioManager: 用户监听设备取消注册失败 - ${deviceId}:`, response.message);
      }
    } catch (error) {
      console.error(`AudioManager: 用户监听设备取消注册异常 - ${deviceId}:`, error);
      // 不抛出错误，因为MQTT取消订阅已经成功，只是后端取消注册失败
    }
  }
  
  /**
   * 开始录音
   */
  async startRecording(): Promise<void> {
    try {
      if (!this.state.activeDeviceId) {
        throw new Error('没有活跃设备，无法开始录音');
      }
      
      console.log('🎤 [测试] AudioManager: 开始录音 - 活跃设备ID:', this.state.activeDeviceId);

      // 开始累积调试音频数据（PTT 结束时下载）
      this.codec2DumpChunks = [];
      this.rawMicDumpChunks = [];

      // 确保音频上下文可用
      if (!this.audioContext) {
        await this.createAudioContext();
      }
      
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // Codec2 上行且浏览器支持 8kHz 时，使用独立 8kHz 录音（浏览器原生重采样，跳过 JS 48k→8k）
      let use8kRecording = false;
      if (this.shouldUplinkUseCodec2ForDevice(this.state.activeDeviceId)) {
        try {
          use8kRecording = await this.ensureCodec2UplinkRecordingContext();
        } catch (e) {
          console.warn('AudioManager: 8kHz 录音上下文检查失败，回退 48kHz', e);
        }
      }

      if (use8kRecording) {
        console.log('🎤 AudioManager: 使用 8kHz 单声道直采录音 (Codec2 优化模式)');
        await this.start8kRecording();
      } else if (this.isWorkletSupported && this.audioWorkletNode) {
        console.log('🎤 AudioManager: 使用 48kHz AudioWorklet 录音');
        await this.startWorkletRecording();
      } else {
        console.log('🎤 AudioManager: 使用 ScriptProcessor 录音 (兼容模式)');
        await this.startMicrophoneRecording();
      }
      
      // 更新系统状态
      this.state = {
        ...this.state,
        isRecording: true,
        devices: {
          ...this.state.devices,
          [this.state.activeDeviceId]: {
            ...this.state.devices[this.state.activeDeviceId],
            isRecording: true
          }
        }
      };
      
      console.log('AudioManager: 录音已开始');
      
    } catch (error) {
      console.error('AudioManager: 开始录音失败:', error);
      throw error;
    }
  }
  
  /**
   * 使用AudioWorklet开始录音
   */
  private async startWorkletRecording(): Promise<void> {
    try {
      if (!this.microphoneModule) {
        throw new Error('麦克风模块未初始化');
      }

      this.syncWorkletStateFromMicrophoneModule();
      
      console.log('🎤 [测试] startWorkletRecording: 开始检查麦克风模块状态');
      
      // 获取麦克风流，如果不存在则重新请求权限
      let mediaStream = await this.microphoneModule.getMediaStream();
      if (!mediaStream) {
        console.log('🎤 [测试] AudioManager: 麦克风流不存在，重新请求麦克风权限...');
        await this.microphoneModule.requestPermission();
        mediaStream = await this.microphoneModule.getMediaStream();
        if (!mediaStream) {
          throw new Error('无法获取麦克风流');
        }
      }
      
      console.log('🎤 [测试] 麦克风流获取成功:', {
        streamId: mediaStream.id,
        active: mediaStream.active,
        tracks: mediaStream.getTracks().length
      });
      
      // 使用MicrophoneModule的AudioWorkletNode，确保音频上下文一致性
      const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode;
      console.log('🎤 [测试] MicrophoneModule AudioWorkletNode状态:', {
        hasWorkletNode: !!microphoneWorkletNode,
        workletInitialized: (this.microphoneModule as any).workletInitialized
      });
      
      if (!microphoneWorkletNode) {
        throw new Error('MicrophoneModule的AudioWorkletNode未初始化');
      }
      
      // 获取MicrophoneModule的音频源
      const microphoneSource = this.microphoneModule.getAudioSource();
      console.log('🎤 [测试] MicrophoneModule音频源状态:', {
        hasAudioSource: !!microphoneSource
      });
      
      if (!microphoneSource) {
        throw new Error('MicrophoneModule的音频源未初始化');
      }
      
      // 连接到MicrophoneModule的AudioWorkletNode
      microphoneSource.connect(microphoneWorkletNode);
      
      console.log('🎤 [测试] AudioManager: 使用MicrophoneModule的AudioWorklet录音已开始');
      
    } catch (error) {
      console.error('AudioManager: AudioWorklet录音启动失败:', error);
      throw error;
    }
  }
  
  /**
   * 使用麦克风模块开始录音
   */
  private async startMicrophoneRecording(): Promise<void> {
    try {
      if (!this.microphoneModule) {
        throw new Error('麦克风模块未初始化');
      }
      
      // 请求麦克风权限并开始录音
      await this.microphoneModule.requestPermission();
      
      console.log('AudioManager: 麦克风模块录音已开始');
      
    } catch (error) {
      console.error('AudioManager: 麦克风模块录音启动失败:', error);
      throw error;
    }
  }
  
  /**
   * 停止录音
   */
  stopRecording(): void {
    try {
      console.log('AudioManager: 停止录音');

      // 导出本次 PTT 累积的两份调试数据（错开触发，避免浏览器拦截连续下载）
      const rawChunks = this.rawMicDumpChunks;
      this.rawMicDumpChunks = null;
      const micLabel = this.uplinkIs8kMono ? 'mic_8k_mono_s16le' : 'mic_48k_stereo_s16le';
      this.downloadDumpChunks(rawChunks, micLabel);
      setTimeout(() => this.downloadCodec2Dump(), 500);

      void this.flushCodec2UplinkRemainder();

      // 停止 8kHz 或 48kHz 录音
      if (this.uplinkIs8kMono) {
        this.stop8kRecording();
      }
      if (this.isWorkletSupported && this.audioWorkletNode) {
        this.stopWorkletRecording();
      }
      
      // 关键优化：停止录音时仅断开录音连接，不销毁MicrophoneModule/MediaStream。
      // 这样可保留麦克风流与worklet预热状态，减少下一次PTT启动时延。
      
      // 更新系统状态
      if (this.state.activeDeviceId) {
        this.state = {
          ...this.state,
          isRecording: false,
          devices: {
            ...this.state.devices,
            [this.state.activeDeviceId]: {
              ...this.state.devices[this.state.activeDeviceId],
              isRecording: false
            }
          }
        };
      }
      
      console.log('AudioManager: 录音已停止');
    } catch (error) {
      console.error('AudioManager: 停止录音失败:', error);
    }
  }
  
  /**
   * 停止AudioWorklet录音
   */
  private stopWorkletRecording(): void {
    try {
      if (this.microphoneModule) {
        // 获取MicrophoneModule的音频源和AudioWorkletNode
        const microphoneSource = this.microphoneModule.getAudioSource();
        const microphoneWorkletNode = (this.microphoneModule as any).audioWorkletNode;
        
        if (microphoneSource && microphoneWorkletNode) {
          // 断开录音连接
          microphoneSource.disconnect(microphoneWorkletNode);
          
          // 重新连接到分析器（保持音频电平监测）
          microphoneSource.connect((this.microphoneModule as any).analyser);
          
          console.log('AudioManager: MicrophoneModule的AudioWorklet录音已停止');
        }
      }
    } catch (error) {
      console.error('AudioManager: 停止AudioWorklet录音失败:', error);
    }
  }
  
  /**
   * 获取当前活跃设备
   */
  getActiveDevice(): string | null {
    return this.activeDeviceId;
  }
  
  /**
   * 获取音频电平
   */
  getAudioLevel(): number {
    if (!this.microphoneModule) {
      return 0;
    }
    return this.microphoneModule.getAudioLevel();
  }
  
  /**
   * 获取系统状态
   */
  getState(): AudioSystemState {
    return { ...this.state };
  }
  
  /**
   * 恢复音频上下文（处理autoplay政策）
   */
  async resumeAudioContext(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        // 音频上下文已恢复
      } catch (error) {
        console.error('AudioManager: 恢复音频上下文失败:', error);
        throw error;
      }
    }
    if (this.codec2PlaybackContext && this.codec2PlaybackContext.state === 'suspended') {
      try {
        await this.codec2PlaybackContext.resume();
      } catch (error) {
        console.warn('AudioManager: 恢复 Codec2 8k 上下文失败:', error);
      }
    }
  }

  /**
   * 在 pointer/touch 事件的同步阶段调用（不要在其前先 await 其它异步）。
   * iOS WebKit：用户激活在 await 网络请求等之后会失效，若之后才 resume，AudioContext 会长期 suspended，Worklet 不跑、无上行音频。
   * 桌面 Chrome 设备模拟仍是桌面策略，故「模拟 iPhone」正常、真机异常。
   */
  kickAudioContextOnUserGesture(): void {
    if (!this.audioContext) {
      return;
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch((err) => {
        console.warn('AudioManager: 用户手势内同步 resume 未成功，后续 prepare 将重试:', err);
      });
    }
    if (this.codec2PlaybackContext && this.codec2PlaybackContext.state === 'suspended') {
      void this.codec2PlaybackContext.resume().catch(() => {
        /* Codec2 下行可在首包时再 resume */
      });
    }
  }

  /**
   * 在发送 PTT MQTT 指令之前调用：resume + 必要时申请麦克风，避免真机 iOS 上手势过期后无法采集。
   */
  async preparePttAudioOnUserGesture(): Promise<void> {
    if (!this.state.isInitialized) {
      console.warn('AudioManager: preparePttAudioOnUserGesture 跳过（音频未初始化）');
      return;
    }
    if (!this.audioContext) {
      return;
    }
    this.kickAudioContextOnUserGesture();
    await this.resumeAudioContext();
    if (this.microphoneModule && !this.microphoneModule.hasPermission()) {
      await this.microphoneModule.requestPermission();
    }
    this.syncWorkletStateFromMicrophoneModule();
  }

  /**
   * 播放接收到的音频数据 - 优化版本，直接使用UserAudioManager的连续播放
   */
  async playReceivedAudio(
    deviceId: string,
    audioData: ArrayBuffer,
    downstreamKind?: MqttAudioDownstreamKind
  ): Promise<void> {
    try {
      this.recordDeviceDownstreamVoiceHint(deviceId, downstreamKind);

      // 在PTT录音期间，不播放接收到的音频数据（半双工通信）
      if (this.state.isRecording) {
        console.log('AudioManager: PTT录音期间，跳过播放接收到的音频数据');
        return;
      }

      if (!this.audioContext) {
        await this.createAudioContext();
      }

      if (!this.audioContext) {
        throw new Error('音频上下文初始化失败');
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const kind: MqttAudioDownstreamKind =
        downstreamKind ??
        (isVoiceDownstreamOpus() ? 'opus' : isVoiceDownstreamCodec2() ? 'codec2' : 'pcm');

      if (kind === 'opus') {
        await this.deliverOpusDownstreamAsPcm(String(deviceId), audioData);
        return;
      }

      if (kind === 'codec2') {
        await this.deliverCodec2DownstreamAsPcm(String(deviceId), audioData);
        return;
      }

      // 优化：直接使用连续播放，避免缓冲延迟
      // 跳过缓冲队列，直接使用AudioWorklet连续播放
      // 使用异步但不等待，避免阻塞
      this.playAudioContinuously(audioData).catch(error => {
        console.error('AudioManager: 连续播放失败', error);
        // 回退到原始播放方式
        this.playRawAudioData(audioData);
      });
      
      // 添加调试日志（降低频率）
      if (Math.random() < 0.01) { // 只记录1%的音频数据
        console.debug(`AudioManager: 音频播放已触发 - 设备ID: ${deviceId}, 数据大小: ${audioData.byteLength}字节`);
      }
      
    } catch (error) {
      console.error(`AudioManager: 播放设备${deviceId}音频失败:`, error);
      // 如果失败，尝试直接播放原始数据
      this.playRawAudioData(audioData);
    }
  }

  private clearCodec2DownstreamState(deviceId: string): void {
    const id = String(deviceId);
    this.codec2DownstreamRemainder.delete(id);
    this.codec2DecodedPcmQueue.delete(id);
    this.codec2DecodedPcmDrainActive.delete(id);
    const opus = this.opusDownstreamSessions.get(id);
    if (opus) {
      opus.close();
      this.opusDownstreamSessions.delete(id);
    }
  }

  /**
   * Opus 下行（audio/device/…/downstream/opus）：载荷为裸 Opus 包（或 2 字节大端长度串联），
   * WebCodecs 解码为 48 kHz mono → 复制为立体声 float → 主上下文连续播放 Worklet。
   */
  private async deliverOpusDownstreamAsPcm(deviceId: string, mqttPayload: ArrayBuffer): Promise<void> {
    const id = String(deviceId);
    let session = this.opusDownstreamSessions.get(id);
    if (!session) {
      session = new OpusWebCodecsDownstreamSession(id);
      this.opusDownstreamSessions.set(id, session);
    }
    const ok = await session.ensureConfigured();
    if (!ok) {
      console.warn('[WaveLink Opus downstream] WebCodecs 不可用或未支持 opus，跳过', { deviceId: id });
      return;
    }
    const packets = splitOpusMqttPayload(new Uint8Array(mqttPayload));
    if (packets.length === 0) {
      return;
    }
    for (let i = 0; i < packets.length; i++) {
      try {
        const stereo = await session.decodePacket(packets[i]);
        await this.enqueueStereoFloat32ContinuousPlayback(stereo);
      } catch (e) {
        console.warn('[WaveLink Opus downstream] 解码失败，已重置该设备会话', { deviceId: id, err: e });
        session.close();
        this.opusDownstreamSessions.delete(id);
        break;
      }
    }
  }

  /**
   * 将已是 48 kHz 交错立体声 float（LRLR…）送入主上下文连续播放 Worklet（与 playAudioContinuously 一致，省略 s16 转换）。
   */
  private async enqueueStereoFloat32ContinuousPlayback(interleavedStereo: Float32Array): Promise<void> {
    if (!this.audioContext) {
      await this.createAudioContext();
    }
    if (!this.audioContext) {
      return;
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    if (!this.isWorkletSupported) {
      this.isWorkletSupported = !!(this.audioContext && 'audioWorklet' in this.audioContext);
    }
    if (!this.continuousPlaybackNode && this.isWorkletSupported) {
      if (!this.isInitializingAudioWorklet) {
        this.isInitializingAudioWorklet = true;
        this.audioWorkletInitPromise = this.initializeContinuousPlayback();
      }
      if (this.audioWorkletInitPromise) {
        try {
          await this.audioWorkletInitPromise;
        } catch (error) {
          console.error('AudioManager: AudioWorklet初始化失败', error);
          this.isWorkletSupported = false;
          return;
        }
      }
    }
    if (!this.isWorkletSupported || !this.continuousPlaybackNode) {
      return;
    }
    try {
      const dataCopy = new Float32Array(interleavedStereo);
      this.continuousPlaybackNode.port.postMessage({ type: 'audioData', data: dataCopy }, [dataCopy.buffer]);
    } catch (error) {
      try {
        this.continuousPlaybackNode.port.postMessage({ type: 'audioData', data: interleavedStereo });
      } catch (fallbackError) {
        console.error('AudioManager: Opus PCM 发送 Worklet 失败', fallbackError);
      }
    }
  }

  /**
   * Codec2 下行：接收任意长度 MQTT 二进制（如 8B 单帧、88B 多帧）→ 与余量合并 → 仅完整帧整批 decodeMany
   * → 8k mono PCM → 入段队列串行写入 8kHz Worklet 环缓依次播放。
   */
  private async deliverCodec2DownstreamAsPcm(deviceId: string, audioData: ArrayBuffer): Promise<void> {
    const id = String(deviceId);
    const frameBytes = getCodec2DownstreamFrameBytes();
    const debugDown =
      String(process.env.REACT_APP_DEBUG_CODEC2_DOWNSTREAM || '').trim() === '1';

    let prev = this.codec2DownstreamRemainder.get(id);
    const incoming = new Uint8Array(audioData);
    let merged: Uint8Array;
    if (prev && prev.length > 0) {
      merged = new Uint8Array(prev.length + incoming.length);
      merged.set(prev, 0);
      merged.set(incoming, prev.length);
    } else {
      merged = incoming;
    }

    const client = getCodec2DecodeClient();
    let firstDecodeError: string | null = null;
    let anyDecodeOk = false;
    let offset = 0;
    const frameBits: ArrayBuffer[] = [];

    while (offset + frameBytes <= merged.length) {
      const bitView = new Uint8Array(frameBytes);
      bitView.set(merged.subarray(offset, offset + frameBytes));
      offset += frameBytes;
      frameBits.push(bitView.buffer);
    }

    const rest = offset < merged.length ? merged.subarray(offset) : new Uint8Array(0);
    if (rest.length > 0) {
      this.codec2DownstreamRemainder.set(id, Uint8Array.from(rest));
    } else {
      this.codec2DownstreamRemainder.delete(id);
    }

    if (frameBits.length === 0) {
      return;
    }

    const batchResult = await client.decodeMany(undefined, frameBits, frameBytes);
    if (!batchResult.ok) {
      firstDecodeError = batchResult.error;
      if (debugDown) {
        console.warn('[WaveLink Codec2 downstream] whole-packet decode failed', {
          deviceId: id,
          frameBytes,
          frames: frameBits.length,
          error: batchResult.error,
        });
      }
    } else {
      anyDecodeOk = true;
      if (debugDown) {
        console.warn('[WaveLink Codec2 downstream] whole-packet decode ok', {
          deviceId: id,
          frameBytes,
          mode: process.env.REACT_APP_CODEC2_MODE || '3200',
          frames: frameBits.length,
          pcmBytes: batchResult.pcm.byteLength,
          sampleRate: 8000,
          channels: 1,
        });
      }
      if (batchResult.pcm.byteLength >= 2) {
        this.enqueueCodec2DecodedPcmForSequentialPlayback(id, batchResult.pcm);
      }
    }

    if (debugDown && merged.length >= frameBytes && !anyDecodeOk) {
      console.warn(
        '[WaveLink Codec2 downstream] 有数据但 0 帧解码成功',
        {
          deviceId: id,
          mergedLen: merged.length,
          frameBytes,
          mode: process.env.REACT_APP_CODEC2_MODE || '3200',
          firstDecodeError: firstDecodeError || 'unknown',
          hint: '请核对 REACT_APP_CODEC2_MODE / REACT_APP_CODEC2_DOWNSTREAM_FRAME_BYTES，并确认 /wasm/codec2/c2dec.wasm 可访问',
        }
      );
    }
  }

  /**
   * 将本包解码得到的 PCM 入队，按设备 FIFO 串行喂环缓（避免多包异步交错写入 Worklet）。
   * 队列过长时丢弃最旧段（控延迟），段数上限见 REACT_APP_CODEC2_DOWNSTREAM_PCM_QUEUE_MAX。
   */
  private enqueueCodec2DecodedPcmForSequentialPlayback(deviceId: string, pcmMono8kS16le: ArrayBuffer): void {
    const id = String(deviceId);
    const maxChunks = getCodec2PlaybackMaxPcmQueueChunks();
    let q = this.codec2DecodedPcmQueue.get(id);
    if (!q) {
      q = [];
      this.codec2DecodedPcmQueue.set(id, q);
    }
    q.push(pcmMono8kS16le.slice(0));
    const debugDown = String(process.env.REACT_APP_DEBUG_CODEC2_DOWNSTREAM || '').trim() === '1';
    while (maxChunks > 0 && q.length > maxChunks) {
      q.shift();
      if (debugDown) {
        console.warn('[WaveLink Codec2 downstream] PCM 段队列溢出，丢弃最旧段', {
          deviceId: id,
          maxChunks,
        });
      }
    }
    void this.drainCodec2DecodedPcmQueue(id);
  }

  private async drainCodec2DecodedPcmQueue(deviceId: string): Promise<void> {
    const id = String(deviceId);
    if (this.codec2DecodedPcmDrainActive.get(id)) {
      return;
    }
    this.codec2DecodedPcmDrainActive.set(id, true);
    try {
      for (;;) {
        const q = this.codec2DecodedPcmQueue.get(id);
        if (!q || q.length === 0) {
          break;
        }
        const pcm = q.shift()!;
        await this.playCodec2DownstreamPcmChunk(id, pcm);
      }
    } finally {
      this.codec2DecodedPcmDrainActive.set(id, false);
      const q = this.codec2DecodedPcmQueue.get(id);
      if (q && q.length > 0) {
        void this.drainCodec2DecodedPcmQueue(id);
      }
    }
  }

  /**
   * Codec2 解码后的 8kHz mono s16le：默认写入独立 8kHz AudioContext 的 Worklet 环缓（连贯播放）；
   * 若环境要求或浏览器不采纳 8kHz，则回退为 JS 上采样到 48k 再走主上下文。
   */
  private async playCodec2DownstreamPcmChunk(deviceId: string, pcmMono8kS16le: ArrayBuffer): Promise<void> {
    const id = String(deviceId);
    if (pcmMono8kS16le.byteLength < 2) {
      return;
    }

    const preCoeff = getCodec2PlaybackPreemphasisCoefficient();
    const pcmWork =
      preCoeff > 0
        ? applyCodec2Mono8kPreemphasisS16le(pcmMono8kS16le, preCoeff)
        : pcmMono8kS16le;

    if (isCodec2DownstreamForceResampleTo48k()) {
      const stereo48 = pcmMono8kToStereo48kS16le(pcmWork);
      if (stereo48.byteLength >= 4) {
        await this.playStereo48PcmDownstream(id, stereo48);
      }
      return;
    }

    const use8k = await this.ensureCodec2PlaybackContext();
    if (!use8k || !this.codec2PlaybackContext) {
      const stereo48 = pcmMono8kToStereo48kS16le(pcmWork);
      if (stereo48.byteLength >= 4) {
        await this.playStereo48PcmDownstream(id, stereo48);
      }
      return;
    }

    if (this.codec2PlaybackContext.state !== 'running') {
      try {
        await this.codec2PlaybackContext.resume();
      } catch (e) {
        console.warn('AudioManager: Codec2 8k 上下文 resume 失败，回退 48k 重采样', e);
        const stereo48 = pcmMono8kToStereo48kS16le(pcmWork);
        if (stereo48.byteLength >= 4) {
          await this.playStereo48PcmDownstream(id, stereo48);
        }
        return;
      }
    }

    if (!this.isWorkletSupported) {
      this.isWorkletSupported = !!(this.codec2PlaybackContext && 'audioWorklet' in this.codec2PlaybackContext);
    }

    if (!this.codec2ContinuousPlaybackNode && this.isWorkletSupported) {
      if (!this.isInitializingCodec2Playback) {
        this.isInitializingCodec2Playback = true;
        this.codec2PlaybackInitPromise = this.initializeCodec2ContinuousPlayback();
      }
      if (this.codec2PlaybackInitPromise) {
        try {
          await this.codec2PlaybackInitPromise;
        } catch (error) {
          console.error('AudioManager: Codec2 8k Worklet 初始化失败', error);
          this.codec2ContinuousPlaybackNode = null;
        } finally {
          this.isInitializingCodec2Playback = false;
          this.codec2PlaybackInitPromise = null;
        }
      }
    }

    if (!this.isWorkletSupported || !this.codec2ContinuousPlaybackNode) {
      const stereo48 = pcmMono8kToStereo48kS16le(pcmWork);
      if (stereo48.byteLength >= 4) {
        await this.playStereo48PcmDownstream(id, stereo48);
      }
      return;
    }

    const float32Data = this.convertMonoS16leToStereoFloat32Interleaved(pcmWork);
    if (!float32Data) {
      return;
    }
    try {
      const dataCopy = new Float32Array(float32Data);
      this.codec2ContinuousPlaybackNode.port.postMessage(
        { type: 'audioData', data: dataCopy },
        [dataCopy.buffer]
      );
      if (String(process.env.REACT_APP_DEBUG_CODEC2_DOWNSTREAM || '').trim() === '1') {
        const sr = this.codec2PlaybackContext.sampleRate;
        console.warn('[WaveLink Codec2 downstream] queued 8k worklet chunk (no JS upsampling)', {
          deviceId: id,
          monoPcmBytes: pcmWork.byteLength,
          contextSampleRateHz: sr,
          approxDurationMs: Number((((pcmWork.byteLength / 2) / sr) * 1000).toFixed(2)),
        });
      }
    } catch (error) {
      console.error('AudioManager: 发送 Codec2 8k 数据到 Worklet 失败', error);
      const stereo48 = pcmMono8kToStereo48kS16le(pcmWork);
      if (stereo48.byteLength >= 4) {
        await this.playStereo48PcmDownstream(id, stereo48);
      }
    }
  }

  /** 浏览器是否可按 8kHz 创建播放上下文；不满足则置位并回退重采样 */
  private async ensureCodec2PlaybackContext(): Promise<boolean> {
    if (isCodec2DownstreamForceResampleTo48k()) {
      return false;
    }
    if (this.codec2PlaybackContextUnsupported) {
      return false;
    }
    if (this.codec2PlaybackContext) {
      return true;
    }
    try {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor({ sampleRate: 8000 });
      const rate = ctx.sampleRate;
      if (Math.abs(rate - 8000) > 60) {
        console.warn(
          `AudioManager: 浏览器未采用 8kHz AudioContext（实际 ${rate}Hz），Codec2 下行将使用 48k 重采样路径`
        );
        await ctx.close();
        this.codec2PlaybackContextUnsupported = true;
        return false;
      }
      this.codec2PlaybackContext = ctx;
      console.log(
        'AudioManager: Codec2 下行使用独立 8kHz AudioContext + Worklet 环缓（不在前端做 8→48 上采样）'
      );
      return true;
    } catch (e) {
      console.warn('AudioManager: 无法创建 8kHz AudioContext，Codec2 下行使用 48k 重采样', e);
      this.codec2PlaybackContextUnsupported = true;
      return false;
    }
  }

  private async initializeCodec2ContinuousPlayback(): Promise<void> {
    if (!this.codec2PlaybackContext || this.codec2ContinuousPlaybackNode) {
      this.isInitializingCodec2Playback = false;
      this.codec2PlaybackInitPromise = null;
      return;
    }
    if (!this.isWorkletSupported) {
      this.isInitializingCodec2Playback = false;
      this.codec2PlaybackInitPromise = null;
      return;
    }
    try {
      await this.codec2PlaybackContext.audioWorklet.addModule(
        publicAssetUrl('/audio-playback-processor.js')
      );
      const sr = this.codec2PlaybackContext.sampleRate;
      const ring8k = getCodec2DownstreamRingInterleavedFloats(sr);
      const min8k = getCodec2DownstreamMinInterleavedSamples8k(sr);
      const aggressiveRing = isAudioPlaybackAggressiveRingStart();
      this.codec2ContinuousPlaybackNode = new AudioWorkletNode(
        this.codec2PlaybackContext,
        'audio-playback-processor',
        {
          processorOptions: {
            sampleRate: sr,
            channels: 2,
            ringBufferSize: ring8k,
            minBufferInterleavedSamples: min8k,
            aggressiveRingStart: aggressiveRing,
          },
          numberOfInputs: 0,
          numberOfOutputs: 1,
          channelCount: 2,
          channelCountMode: 'explicit',
          channelInterpretation: 'speakers',
        }
      );
      this.codec2ContinuousPlaybackNode.port.onmessage = (event) => {
        const { type, ...data } = event.data;
        if (type === 'status' && data.underrunCount > 0 && data.underrunCount % 100 === 0) {
          console.warn('AudioManager: Codec2 8k Worklet 缓冲区不足', data);
        }
      };
      this.codec2ContinuousPlaybackNode.connect(this.codec2PlaybackContext.destination);
      console.log('AudioManager: Codec2 8kHz 连续播放 Worklet 已初始化');
    } catch (error) {
      console.error('AudioManager: 初始化 Codec2 8k Worklet 失败', error);
      this.codec2ContinuousPlaybackNode = null;
      throw error;
    } finally {
      this.isInitializingCodec2Playback = false;
      this.codec2PlaybackInitPromise = null;
    }
  }

  /** 8k mono s16le → 同采样率立体声交错 float（L=R），供 AudioWorklet 消费 */
  private convertMonoS16leToStereoFloat32Interleaved(mono: ArrayBuffer): Float32Array | null {
    try {
      const nIn = Math.floor(mono.byteLength / 2);
      if (nIn <= 0) {
        return null;
      }
      const pcm = new Int16Array(mono, 0, nIn);
      const out = new Float32Array(nIn * 2);
      const scale = 1.0 / 32768.0;
      for (let i = 0; i < nIn; i++) {
        const v = pcm[i] * scale;
        out[i * 2] = v;
        out[i * 2 + 1] = v;
      }
      return out;
    } catch (e) {
      console.error('AudioManager: mono s16le 转 float 失败', e);
      return null;
    }
  }

  /** 将已是 48kHz 立体声 s16le 的 PCM 送入现有连续播放链 */
  private async playStereo48PcmDownstream(deviceId: string, audioData: ArrayBuffer): Promise<void> {
    try {
      if (!this.audioContext) {
        await this.createAudioContext();
      }
      if (!this.audioContext) {
        return;
      }
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      await this.playAudioContinuously(audioData);
      console.warn('[WaveLink Codec2 downstream] queued continuous worklet chunk', {
        deviceId: String(deviceId),
        stereo48Bytes: audioData.byteLength,
        approxDurationMs: Number((((audioData.byteLength / 4) / 48000) * 1000).toFixed(2)),
      });
    } catch (e) {
      console.error(`AudioManager: Codec2 下行 PCM 播放失败 device=${deviceId}`, e);
      this.playRawAudioData(audioData);
    }
  }
  
  /**
   * 将音频数据添加到缓冲队列
   */
  private addToAudioBuffer(deviceId: string, audioData: ArrayBuffer): void {
    if (!this.audioBufferQueue.has(deviceId)) {
      this.audioBufferQueue.set(deviceId, []);
    }
    
    const queue = this.audioBufferQueue.get(deviceId)!;
    queue.push(audioData);
    
    // 计算当前队列的总时长
    const totalBytes = queue.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const totalFrames = Math.floor(totalBytes / 4); // 4字节/帧 (2声道×2字节)
    const totalDurationMs = (totalFrames / 48000) * 1000;
    
    // 设备${deviceId}音频缓冲 - 队列:${queue.length}, 时长:${totalDurationMs.toFixed(1)}ms
    
    // 智能播放策略：
    // 1. 如果达到最小时长，播放缓冲的音频
    // 2. 如果超过最大缓冲时长，强制播放避免卡顿
    // 3. 如果队列中有足够多的片段，也播放（避免等待）
    if (totalDurationMs >= this.MIN_AUDIO_DURATION_MS || 
        totalDurationMs >= this.MAX_BUFFER_DURATION_MS ||
        queue.length >= 10) { // 如果队列中有10个或更多片段，也播放
      this.playBufferedAudio(deviceId);
    }
  }
  
  /**
   * 连续播放音频数据 - 使用AudioWorklet实现无卡顿播放
   */
  private async playAudioContinuously(audioData: ArrayBuffer): Promise<void> {
    try {
      if (!this.audioContext || this.audioContext.state !== 'running') {
        this.playRawAudioData(audioData);
        return;
      }
      
      // 检查AudioWorklet支持（使用现有的isWorkletSupported）
      if (!this.isWorkletSupported) {
        this.isWorkletSupported = !!(this.audioContext && 'audioWorklet' in this.audioContext);
      }
      
      // 确保AudioWorklet已初始化
      if (!this.continuousPlaybackNode && this.isWorkletSupported) {
        if (!this.isInitializingAudioWorklet) {
          this.isInitializingAudioWorklet = true;
          this.audioWorkletInitPromise = this.initializeContinuousPlayback();
        }
        
        if (this.audioWorkletInitPromise) {
          try {
            await this.audioWorkletInitPromise;
          } catch (error) {
            console.error('AudioManager: AudioWorklet初始化失败', error);
            this.isWorkletSupported = false;
            this.playRawAudioData(audioData);
            return;
          }
        }
      }
      
      // 如果AudioWorklet不可用，回退到原始播放方式
      if (!this.isWorkletSupported || !this.continuousPlaybackNode) {
        this.playRawAudioData(audioData);
        return;
      }
      
      // 将ArrayBuffer转换为Float32Array并发送到AudioWorklet
      const float32Data = this.convertAudioToFloat32Array(audioData);
      if (float32Data && this.continuousPlaybackNode) {
        try {
          const dataCopy = new Float32Array(float32Data);
          this.continuousPlaybackNode.port.postMessage({
            type: 'audioData',
            data: dataCopy
          }, [dataCopy.buffer]);
        } catch (error) {
          try {
            this.continuousPlaybackNode.port.postMessage({
              type: 'audioData',
              data: float32Data
            });
          } catch (fallbackError) {
            console.error('AudioManager: 发送音频数据到AudioWorklet失败', fallbackError);
            this.playRawAudioData(audioData);
          }
        }
      } else {
        this.playRawAudioData(audioData);
      }
      
    } catch (error) {
      console.error('AudioManager: 连续播放失败', error);
      this.playRawAudioData(audioData);
    }
  }
  
  /**
   * 初始化连续播放节点 - 使用AudioWorklet
   */
  private async initializeContinuousPlayback(): Promise<void> {
    if (!this.audioContext || this.continuousPlaybackNode || !this.isWorkletSupported) {
      return;
    }
    
    try {
      await this.audioContext.audioWorklet.addModule(publicAssetUrl('/audio-playback-processor.js'));
      
      const ringFloats = getAudioPlaybackRingBufferFloats();
      const minRingSamples = getAudioPlaybackMinInterleavedSamples();
      const aggressiveRing = isAudioPlaybackAggressiveRingStart();
      this.continuousPlaybackNode = new AudioWorkletNode(this.audioContext, 'audio-playback-processor', {
        processorOptions: {
          sampleRate: this.audioContext.sampleRate,
          channels: 2,
          ringBufferSize: ringFloats,
          minBufferInterleavedSamples: minRingSamples,
          aggressiveRingStart: aggressiveRing,
        },
        numberOfInputs: 0,
        numberOfOutputs: 1,
        channelCount: 2,
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers'
      });
      
      this.continuousPlaybackNode.port.onmessage = (event) => {
        const { type, ...data } = event.data;
        if (type === 'status' && data.underrunCount > 0 && data.underrunCount % 100 === 0) {
          console.warn('AudioManager: AudioWorklet缓冲区不足', data);
        }
      };
      
      this.continuousPlaybackNode.connect(this.audioContext.destination);
      this.isInitializingAudioWorklet = false;
      this.audioWorkletInitPromise = null;
      
      console.log('AudioManager: AudioWorklet连续播放节点已初始化');
      
    } catch (error) {
      console.error('AudioManager: 初始化AudioWorklet连续播放节点失败', error);
      this.isWorkletSupported = false;
      this.continuousPlaybackNode = null;
      this.isInitializingAudioWorklet = false;
      this.audioWorkletInitPromise = null;
      throw error;
    }
  }
  
  /**
   * 将ArrayBuffer转换为Float32Array（双声道交错格式）
   */
  private convertAudioToFloat32Array(audioData: ArrayBuffer): Float32Array | null {
    try {
      const pcm16Array = new Int16Array(audioData);
      const sampleCount = Math.floor(pcm16Array.length / 2);
      
      if (sampleCount <= 0) {
        return null;
      }
      
      const float32Array = new Float32Array(sampleCount * 2);
      const normalizationFactor = 1.0 / 32768.0;
      const length = Math.min(sampleCount, Math.floor(pcm16Array.length / 2));
      
      for (let i = 0; i < length; i++) {
        const baseIndex = i * 2;
        const outputIndex = i * 2;
        float32Array[outputIndex] = pcm16Array[baseIndex] * normalizationFactor;
        float32Array[outputIndex + 1] = pcm16Array[baseIndex + 1] * normalizationFactor;
      }
      
      if (length < sampleCount) {
        for (let i = length; i < sampleCount; i++) {
          const outputIndex = i * 2;
          float32Array[outputIndex] = 0;
          float32Array[outputIndex + 1] = 0;
        }
      }
      
      return float32Array;
    } catch (error) {
      console.error('AudioManager: 转换音频数据失败', error);
      return null;
    }
  }
  
  /**
   * 播放缓冲的音频数据
   */
  private playBufferedAudio(deviceId: string): void {
    const queue = this.audioBufferQueue.get(deviceId);
    if (!queue || queue.length === 0) {
      return;
    }
    
    // 合并所有音频数据
    const totalBytes = queue.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const mergedBuffer = new ArrayBuffer(totalBytes);
    const mergedView = new Uint8Array(mergedBuffer);
    
    let offset = 0;
    for (const buffer of queue) {
      mergedView.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }
    
    // 播放设备${deviceId}缓冲音频 - 字节:${totalBytes}, 片段:${queue.length}
    
    // 清空队列
    queue.length = 0;
    
    // 直接播放合并后的音频数据，确保连续性
    this.playRawAudioData(mergedBuffer);
    this.lastPlayTime.set(deviceId, Date.now());
  }

  /**
   * 播放原始音频数据（当解码失败时的备选方案）
   */
  private playRawAudioData(audioData: ArrayBuffer): void {
    try {
      if (!this.audioContext) {
        console.warn('AudioManager: 音频上下文未初始化，无法播放原始音频数据');
        return;
      }

      // 音频数据格式：48kHz, 16位, 双声道(交错), 小端字节序, 有符号
      const sampleRate = 48000;
      const channels = 2;
      const bytesPerSample = 2; // 16位 = 2字节
      const bytesPerFrame = bytesPerSample * channels; // 每帧4字节 (L+R)
      const totalFrames = Math.floor(audioData.byteLength / bytesPerFrame); // 帧数，每个帧包含左右声道
      
      // 音频数据: ${audioData.byteLength}字节, ${totalFrames}帧, ${sampleRate}Hz, ${channels}声道
      
      if (totalFrames === 0) {
        console.warn('AudioManager: 音频数据长度不足，无法播放');
        return;
      }
      
      // 检查音频数据内容（用于调试）
      const dataView = new DataView(audioData);
      let zeroCount = 0;
      for (let i = 0; i < Math.min(32, audioData.byteLength); i++) {
        if (dataView.getUint8(i) === 0) zeroCount++;
      }
      
      const audioBuffer = this.audioContext.createBuffer(channels, totalFrames, sampleRate);
      const leftChannel = audioBuffer.getChannelData(0);
      const rightChannel = audioBuffer.getChannelData(1);
      
      
      for (let i = 0; i < totalFrames; i++) {
        const frameOffset = i * bytesPerFrame;
        
        // 检查边界
        if (frameOffset + 3 >= audioData.byteLength) {
          console.warn(`AudioManager: 帧 ${i} 超出数据边界，停止处理`);
          break;
        }
        
        // 左声道：索引 frameOffset
        const leftSample = dataView.getInt16(frameOffset, true); // 小端序
        leftChannel[i] = leftSample / 32768.0; // 归一化到[-1, 1]
        
        // 右声道：索引 frameOffset + 2
        const rightSample = dataView.getInt16(frameOffset + 2, true); // 小端序
        rightChannel[i] = rightSample / 32768.0; // 归一化到[-1, 1]
      }
      
      // 创建音频源并播放
      const source = this.audioContext.createBufferSource();
      // 设置音频数据
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      source.start();
      
      // 播放音频成功 - 时长:${audioBuffer.duration.toFixed(3)}s
      
      // 添加播放结束监听
      source.onended = () => {
        // 音频播放结束
      };
      
    } catch (error) {
      console.error('AudioManager: 播放原始音频数据失败:', error);
    }
  }
  
  /**
   * 事件监听
   */
  on<K extends keyof AudioManagerEvents>(
    event: K,
    callback: AudioManagerEvents[K]
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }
  
  /**
   * 移除事件监听
   */
  off<K extends keyof AudioManagerEvents>(
    event: K,
    callback: AudioManagerEvents[K]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  /**
   * 触发事件
   */
  private emit<K extends keyof AudioManagerEvents>(
    event: K,
    ...args: any[]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`AudioManager: 事件处理错误 (${event}):`, error);
        }
      });
    }
  }
  
  /**
   * 销毁资源
   */
  async dispose(): Promise<void> {
    console.log('AudioManager: 销毁资源...');

    this.deviceStatusMqttUnsub?.();
    this.deviceStatusMqttUnsub = null;

    // 停止所有设备监听（仅清理本地状态，不调用需要认证的API）
    if (this.state.activeDeviceId) {
      try {
        const stoppedDeviceId = this.state.activeDeviceId; // 在清除前保存
        
        // 取消前端MQTT订阅
        try {
          const { mqttService } = await import('../mqttService');
          await mqttService.unsubscribeFromDeviceAudio(stoppedDeviceId);
          console.log(`AudioManager: 前端MQTT订阅已取消 - 设备ID: ${stoppedDeviceId}`);
        } catch (mqttError) {
          console.warn(`AudioManager: 取消前端MQTT订阅失败 - 设备ID: ${stoppedDeviceId}:`, mqttError);
          // MQTT取消订阅失败不影响其他清理操作
        }
        
        // 更新系统状态
        this.activeDeviceId = null; // 修复：同时清除实例变量
        this.state = {
          ...this.state,
          devices: {
            ...this.state.devices,
            [stoppedDeviceId]: {
              ...this.state.devices[stoppedDeviceId],
              isActive: false,
              isPlaying: false
            }
          },
          activeDeviceId: null,
          isListening: false
        };
        
        console.log(`AudioManager: 已停止监听设备 ${stoppedDeviceId}（仅本地清理）`);
      } catch (error) {
        console.error('AudioManager: 停止设备监听失败:', error);
      }
    }
    
    // 录音用 AudioWorklet 节点由 MicrophoneModule 创建并在 mic.dispose 中断开，此处仅清除引用
    this.audioWorkletNode = null;
    
    // 销毁麦克风模块
    if (this.microphoneModule) {
      this.microphoneModule.dispose();
      this.microphoneModule = null;
    }
    
    // 关闭音频上下文
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        console.error('AudioManager: 关闭音频上下文失败:', error);
      }
      this.audioContext = null;
    }
    
    // 清理连续播放节点
    if (this.continuousPlaybackNode) {
      try {
        this.continuousPlaybackNode.disconnect();
        this.continuousPlaybackNode.port.postMessage({ type: 'clear' });
        this.continuousPlaybackNode = null;
        console.log('AudioManager: 连续播放节点已清理');
      } catch (error) {
        console.error('AudioManager: 清理连续播放节点失败:', error);
      }
    }
    if (this.codec2ContinuousPlaybackNode) {
      try {
        this.codec2ContinuousPlaybackNode.disconnect();
        this.codec2ContinuousPlaybackNode.port.postMessage({ type: 'clear' });
      } catch (error) {
        console.error('AudioManager: 清理 Codec2 8k 播放节点失败:', error);
      }
      this.codec2ContinuousPlaybackNode = null;
    }
    if (this.codec2PlaybackContext) {
      try {
        await this.codec2PlaybackContext.close();
      } catch (error) {
        console.error('AudioManager: 关闭 Codec2 8k 上下文失败:', error);
      }
      this.codec2PlaybackContext = null;
    }
    this.codec2PlaybackContextUnsupported = false;
    this.isInitializingCodec2Playback = false;
    this.codec2PlaybackInitPromise = null;
    this.codec2DecodedPcmQueue.clear();
    this.codec2DecodedPcmDrainActive.clear();

    // 清理 8kHz 上行录音上下文
    this.stop8kRecording();
    if (this.codec2UplinkRecordingWorklet) {
      try { this.codec2UplinkRecordingWorklet.disconnect(); } catch { /* ignore */ }
      this.codec2UplinkRecordingWorklet = null;
    }
    if (this.codec2UplinkRecordingGain) {
      try { this.codec2UplinkRecordingGain.disconnect(); } catch { /* ignore */ }
      this.codec2UplinkRecordingGain = null;
    }
    if (this.codec2UplinkRecordingCtx) {
      try { await this.codec2UplinkRecordingCtx.close(); } catch { /* ignore */ }
      this.codec2UplinkRecordingCtx = null;
    }
    this.codec2UplinkRecording8kUnsupported = false;
    
    // 清理所有定时器
    if (this.audioDataReceivedTimeout) {
      clearTimeout(this.audioDataReceivedTimeout);
      this.audioDataReceivedTimeout = null;
    }
    
    // 清理所有音频缓冲区
    this.audioBufferQueue.clear();
    this.codec2DownstreamRemainder.clear();
    this.codec2Uplink8kRemainder = new Uint8Array(0);
    this.codec2UplinkBitBatch = new Uint8Array(0);
    this.deviceDownstreamVoiceHint.clear();
    this.opusDownstreamSessions.forEach((s) => s.close());
    this.opusDownstreamSessions.clear();
    disposeCodec2DecodeClient();
    disposeCodec2EncodeClient();
    console.log('AudioManager: 音频缓冲区已清空');
    
    // 清理网络监控数据
    this.networkLatency.clear();
    this.packetLossRate.clear();
    this.lastPacketTime.clear();
    
    // 清理播放状态
    this.isPlaying.clear();
    this.lastPlayTime.clear();
    
    // 重置AudioWorklet状态
    this.workletInitialized = false;
    this.isInitializingAudioWorklet = false;
    this.audioWorkletInitPromise = null;
    
    // 重置状态
    this.state.isInitialized = false;
    this.activeDeviceId = null;
    this.state.activeDeviceId = null;
    this.state.isConnected = false;
    this.state.isRecording = false;
    this.state.isListening = false;
    this.state.devices = {};
    
    // 清理事件监听器
    this.eventListeners.clear();
    
    console.log('AudioManager: 销毁完成');
  }


}

// 导出单例实例
export const audioManager = AudioManager.getInstance();
